/**
 * TotalConnectRTC - Real-Time Communications Modal
 * 
 * The primary voice/video calling component for TotalAccess platform.
 * Supports internal LiveKit calls, SIP/PABX integration, and multi-platform
 * operation across web, desktop (Tauri), and mobile (Capacitor).
 * 
 * Architecture:
 * - Lifecycle integration via capacitorLifecycleManager
 * - State management and WebSocket signaling for call coordination
 * - UI rendering delegated to modular components in @/components/call
 * - Hooks handle LiveKit, controls, and notifications
 * - CallStateManager handles persistent call state for Capacitor cold starts
 *
 * SIP + RTC (how this compares to a classic SIP softphone like Tragofone):
 * - Internal calls: browser ↔ browser via LiveKit WebRTC only (/api/v1/calls initiate).
 * - PSTN / carrier / desk-phone legs: SIP is terminated at the Kamailio edge → LiveKit SIP.
 *   The browser never does SIP REGISTER/RTP to the PBX; it joins the same LiveKit room as the
 *   SIP participant. Outbound: initiateOutboundCall creates the room + token, then the carrier
 *   bridges the callee into that room (see /api/v1/sip/outbound TwiML → LiveKit SIP URI).
 * - Feature parity targets: blind/attended transfer, hold/MOH, park/retrieve, DTMF, voicemail,
 *   CRM screen-pop, push — wired here where APIs exist; softphone-style multi-line UI reflects
 *   logical lines (primary + consultation). True parallel calls still need a second LiveKit room.
 * 
 * LIFECYCLE INTEGRATION (February 2026):
 * - Auto-reconnects WebSocket when app resumes from background
 * - Maintains call state during app state transitions
 * - Network-aware reconnection handling
 * 
 * CAPACITOR COLD START FIX (January 2026):
 * When user taps "Answer" from FCM notification while app is backgrounded,
 * Android may have killed the WebView. The CallStateManager persists call
 * data to localStorage and provides a subscription model to ensure the
 * call data survives the cold start initialization process.
 * 
 * @author TotalAccess Development Team
 * @date February 2026
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/calls.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/calls)
 */

import UserDocsLink from '@/components/shared/UserDocsLink';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { buildApiUrl, isCapacitor, isTauri } from '@/lib/platformUtils';
import { isIntentionalDisconnectError, wsClient } from '@/lib/websocket/TotalAccessWebSocket';
import { format, addHours } from 'date-fns';
import { Phone, Users, Clock, Grid3X3, Settings, Video, X } from 'lucide-react';
import { useModalState } from '@/contexts/ModalStateContext';
import { useBadge } from '@/contexts/BadgeContext';
import { useTauriCall } from '@/hooks/useTauriCall';
import { notify } from '@/lib/notifications';
import toast from 'react-hot-toast';
import LockScreen from '@/plugins/LockScreen';
import { usePendingCall, callStateManager } from '@/lib/callStateManager';
import { useCapacitorLifecycle } from '@/hooks/useCapacitorLifecycle';
import {
  Room,
  RoomEvent,
  ConnectionState,
  Track,
  RemoteParticipant,
  RemoteTrackPublication,
  LocalParticipant,
  createLocalTracks,
  type LocalTrack,
  VideoPresets,
} from 'livekit-client';
import { getLiveKitUrl } from '@/lib/livekitConfig';
import { openCallPopup, storeCallParams, prepareCallRoomUrl, preflightMediaPermissions, sendCallPopupCommand, createCallInstanceId } from '@/lib/callPopup';
import { EventModal } from '@/app/client-zone/modules/total-connect/components/EnhancedCalendarModule';
import { useCalendarStore } from '@/app/client-zone/modules/total-connect/stores';
import type { CalendarEvent } from '@/app/client-zone/modules/total-connect/types';
import { canAccessContacts } from '@/lib/contactsUtils';
import { useRouter } from 'next/navigation';

// Import modular call components
import {
  // Hooks
  useCallSession,
  useCallControls,
  useCallNotifications,
  useCallPlugins,
  
  // UI Components
  RingingUI,
  InCallUI,
  ContactsView,
  RecentsView,
  DialPadView,
  CrmDialContactsList,
  fetchCrmContactsForDialer,
} from '@/components/call';

import type {
  CallState,
  CallType,
  ViewMode,
  TabType,
  CallModalMode,
  OutgoingCallProgress,
  OperationsUser,
  CallLog,
  CrmDialContactRow,
  CallParticipant,
  CallActivityEntry,
  CallReaction,
  IncomingCall,
  PendingCallTarget,
  PendingGroupCall,
  SipCallData,
  ScreenPopData,
  CallSettings,
  ActiveSession,
  BreakoutRoom,
  GroupCallInfo,
  SipExtension,
  ParkingSlot,
  MeetingSettings,
  WaitingParticipant,
  PickupGroupCall,
  AttendedTransferState,
} from '@/components/call';

import type { ActiveCallLine } from '@/components/call/types';
import { CallSettingsPanel } from '@/components/call/ui/CallSettingsPanel';
import { PhoneContactsModal } from '@/components/call/ui/PhoneContactsModal';
import { VoicemailPanel } from '@/components/call/ui/VoicemailPanel';
import CallWhiteboard, { type DrawingElement } from '@/components/CallWhiteboard';
import BreakoutRoomsModal from '@/components/BreakoutRoomsModal';

import { TotalConnectRtcIdleShell } from '@/components/total-connect-rtc/TotalConnectRtcIdleShell';
import { TotalConnectIdleStageAside } from '@/components/total-connect-rtc/TotalConnectIdleStageAside';
import { TotalConnectIdleDialCompanion } from '@/components/total-connect-rtc/TotalConnectIdleDialCompanion';
import {
  TotalConnectCallsListToolbar,
  type CallsContactsSort,
  type CallsRecentsScope,
  type CallsRecentsTimeSort,
} from '@/components/total-connect-rtc/TotalConnectCallsListToolbar';
import { useDialPadSubmit } from '@/components/total-connect-rtc/useDialPadSubmit';
import { TotalConnectClientSelector } from '@/components/total-connect-rtc/TotalConnectClientSelector';
import { useAccessiblePabxClients } from '@/hooks/useAccessiblePabxClients';
import {
  handleTotalConnectRtcWebSocketMessage,
  type TotalConnectRtcWsHandlerContext,
} from '@/components/total-connect-rtc/handleTotalConnectRtcWebSocketMessage';
import { addDeclinedIncomingCallIds } from '@/components/total-connect-rtc/wsInboundEnvelope';
import CapacitorModalFilterHeader from '@/components/CapacitorModalFilterHeader';
import { buildDialPadSuggestions } from '@/components/call/dialPadSuggestionUtils';
import {
  TotalConnectMeetingsView,
  type MeetingJoinTarget,
  resolveMeetingJoinTarget,
} from '@/components/total-connect-rtc/TotalConnectMeetingsView';

function scheduleDismissNativeCallConnectingOverlay(): void {
  if (typeof window === 'undefined') return;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      (window as Window & { __hideCallConnectingUI?: () => void }).__hideCallConnectingUI?.();
    });
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === 'string' ? error : 'Unknown error';
}

// ============================================
// Props Interface
// ============================================

interface TotalConnectRTCProps {
  isOpen: boolean;
  onClose: () => void;
  onShowDashboard?: () => void;
  incomingCall?: IncomingCall | null;
  pendingCallTarget?: PendingCallTarget | null;
  pendingGroupCall?: PendingGroupCall | null;
  onPendingCallHandled?: () => void;
  onPendingGroupCallHandled?: () => void;
  onMissedCallsChange?: (count: number) => void;
  onIncomingCall?: (call: IncomingCall) => void;
  onClearIncomingCall?: () => void;
  /** Opens Header call modal when handling persisted/native pending calls before UI paint */
  onEnsureCallModalOpen?: () => void;
  /**
   * Embedded layout fills the parent (no backdrop / slide-over). Used by the standalone
   * Tauri Total Connect window suite rail.
   */
  layoutVariant?: 'slideover' | 'embedded';
  /** Caller asserts Total Connect is licensed/enabled — currently informational
   *  for embedded layouts; kept optional so slide-over callers are unaffected. */
  hasTotalConnect?: boolean;
}

// ============================================
// Main Component
// ============================================

/** SIP bridge legs identify as `sip-*`/`sip_*` prefixes or bare E.164 numbers. */
function isSipParticipantIdentity(identity: string | undefined): boolean {
  const id = identity || '';
  return (
    id.startsWith('sip-') ||
    id.startsWith('sip_') ||
    id.startsWith('+') ||
    /^\d{10,15}$/.test(id)
  );
}

/**
 * A SIP bridge leg is "still ringing" while sip.callStatus reports a
 * pre-answer state (dialing/ringing/hangup). Legs without the attribute are
 * treated as answered so gateways that don't publish it keep working.
 */
function isSipLegStillRinging(attributes?: Record<string, string>): boolean {
  const status = attributes?.['sip.callStatus'];
  return status === 'dialing' || status === 'ringing' || status === 'hangup';
}

export default function TotalConnectRTC({
  isOpen,
  onClose,
  onShowDashboard,
  incomingCall,
  pendingCallTarget,
  pendingGroupCall,
  onPendingCallHandled,
  onPendingGroupCallHandled,
  onMissedCallsChange,
  onIncomingCall,
  onClearIncomingCall,
  onEnsureCallModalOpen,
  layoutVariant = 'slideover',
}: TotalConnectRTCProps) {
  // ============================================
  // Auth & Theme
  // ============================================
  const { user, client } = useAuth();
  const router = useRouter();
  const { currentTheme, clientBranding } = useTheme();
  const { registerBackHandler, unregisterBackHandler } = useModalState();
  const { getCallModalMode, hasAccess, loading: moduleAccessLoading } = useModuleAccess();
  // Multi-client PABX access — all clients where user has total-pabx and/or total-connect.
  // The selected client drives outbound calling context (extensions, parking, screen-pop).
  const {
    clients: accessiblePabxClients,
    loading: loadingAccessibleClients,
    selectedClientId: selectedPabxClientId,
    selectedClient: selectedPabxClient,
    selectClient: selectPabxClient,
    hasMultipleClients: hasMultiplePabxClients,
    callModalMode: selectedClientCallModalMode,
  } = useAccessiblePabxClients();
  const { badgeCounts } = useBadge();
  const { enableCallMode, disableCallMode, focusWindow, isTauriDesktop } = useTauriCall();
  const callStateRef = useRef<CallState>('idle');
  const outgoingInternalCallRef = useRef(false);
  const queuedOutgoingRingingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOpenRef = useRef(isOpen);
  const leaveRoomAfterTransferRef = useRef<(() => void) | null>(null);
  const endCallRef = useRef<((reason?: string) => Promise<void>) | null>(null);
  
  const isDark = currentTheme.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || currentTheme.colors.primary;

  // ============================================
  // Lifecycle Integration
  // ============================================
  // Track app state changes for WebSocket reconnection and call state management
  const wsReconnectPendingRef = useRef(false);
  
  const { appState, isOnline, isActive } = useCapacitorLifecycle({
    onResume: () => {
      // wsClient handles its own reconnection via lifecycle.ts
      // Just ensure it's connected
      if (!wsClient.isConnected()) {
        wsReconnectPendingRef.current = true;
        wsClient.connect().catch(err => {
          if (!isIntentionalDisconnectError(err)) {
            console.warn('[TotalConnectRTC] Resume reconnect failed:', err);
          }
        });
      }
    },
    onPause: () => {
      // wsClient handles background disconnect via lifecycle.ts
      // Mark reconnect as no longer pending — handleAppResume() will re-trigger
      wsReconnectPendingRef.current = false;
    },
    onNetworkChange: (online, networkState) => {
      if (online && !wsClient.isConnected()) {
        wsClient.connect().catch(err => {
          if (!isIntentionalDisconnectError(err)) {
            console.warn('[TotalConnectRTC] Network reconnect failed:', err);
          }
        });
      }
    }
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleBack = (): boolean => {
      if (!isOpenRef.current) {
        return false;
      }

      if (callStateRef.current === 'idle') {
        if (onShowDashboard) {
          onShowDashboard();
        } else {
          onClose();
        }
      }

      return true;
    };

    registerBackHandler(handleBack);
    return () => unregisterBackHandler(handleBack);
  }, [isOpen, onClose, onShowDashboard, registerBackHandler, unregisterBackHandler]);

  // ============================================
  // Capacitor Cold Start Call Handling
  // ============================================
  // Use the CallStateManager to handle calls that arrive during cold start
  // This solves the problem of deep links not surviving app initialization
  const { 
    pendingCall: coldStartPendingCall, 
    confirmHandled: confirmColdStartHandled,
  } = usePendingCall();
  
  // Track if we've handled the cold start call to prevent duplicate processing
  const coldStartHandledRef = useRef<string | null>(null);
  // Track when this component mounted — used to detect stale sessions
  const componentMountTimeRef = useRef<number>(Date.now());

  // ============================================
  // Module Access & Tabs
  // ============================================
  // When the user has PABX/Connect access across multiple clients, compute the
  // call modal mode from the selected client's module access. Fall back to the
  // active-client-based mode when the accessible-clients hook is loading or
  // the user has only one accessible client.
  const activeClientCallModalMode = useMemo(
    () => getCallModalMode(),
    [getCallModalMode],
  ) as CallModalMode;
  const callModalMode = useMemo(() => {
    if (hasMultiplePabxClients && !loadingAccessibleClients) {
      return selectedClientCallModalMode;
    }
    return activeClientCallModalMode;
  }, [hasMultiplePabxClients, loadingAccessibleClients, selectedClientCallModalMode, activeClientCallModalMode]);

  // ============================================
  // Core State
  // ============================================
  const [viewMode, setViewMode] = useState<ViewMode>('contacts');
  const [activeTab, setActiveTab] = useState<TabType>('contacts');
  /** When SIP: team vs CRM phone book under Contacts tab */
  const [contactsSubTab, setContactsSubTab] = useState<'team' | 'crm'>('team');
  const [searchQuery, setSearchQuery] = useState('');
  const [recentsScope, setRecentsScope] = useState<CallsRecentsScope>('all');
  const [recentsTimeSort, setRecentsTimeSort] = useState<CallsRecentsTimeSort>('newest');
  /** When true, Recents shows calls from ALL accessible PABX clients (merged). */
  const [recentsAllClients, setRecentsAllClients] = useState(false);
  const [contactsSort, setContactsSort] = useState<CallsContactsSort>('name_asc');
  const [callState, setCallState] = useState<CallState>('idle');
  const [callType, setCallType] = useState<CallType>('voice');
  const [callDuration, setCallDuration] = useState(0);
  const [outgoingCallProgress, setOutgoingCallProgress] = useState<OutgoingCallProgress>('idle');
  const [selectedUser, setSelectedUser] = useState<OperationsUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // ============================================
  // Data State
  // ============================================
  const [operationsUsers, setOperationsUsers] = useState<OperationsUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loadingCallLogs, setLoadingCallLogs] = useState(false);
  const [missedCallsCount, setMissedCallsCount] = useState(0);
  
  // ============================================
  // SIP/PABX State
  // ============================================
  const [dialPadNumber, setDialPadNumber] = useState('');
  const [isDialingPSTN, setIsDialingPSTN] = useState(false);
  // Local dial history for SIP/PSTN outbound numbers (server call-logs may not include SIP calls)
  const [, setDialHistory] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('totalconnect_dial_history');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [sipCallData, setSipCallData] = useState<SipCallData | null>(null);
  // Visitor-management gate call — callee UI shows authorize/deny keypad actions
  const [isGateCallIncoming, setIsGateCall] = useState(false);
  const sipCallDataRef = useRef<SipCallData | null>(null);
  /** Second simultaneous SIP / LiveKit leg */
  const [sipCallDataSecondary, setSipCallDataSecondary] = useState<SipCallData | null>(null);
  const [selectedUserSecondary, setSelectedUserSecondary] = useState<OperationsUser | null>(null);
  const [screenPopDataSecondary, setScreenPopDataSecondary] = useState<ScreenPopData | null>(null);
  const [focusedLine, setFocusedLine] = useState<'primary' | 'secondary'>('primary');
  const [secondLineIncoming, setSecondLineIncoming] = useState<IncomingCall | null>(null);
  const [crmDialerRows, setCrmDialerRows] = useState<CrmDialContactRow[]>([]);
  const [callSettings, setCallSettings] = useState<CallSettings | null>(null);
  const [extensionList, setExtensionList] = useState<SipExtension[]>([]);
  const [loadingExtensions, setLoadingExtensions] = useState(false);
  const [parkingSlots, setParkingSlots] = useState<ParkingSlot[]>([]);
  const [loadingParkingSlots, setLoadingParkingSlots] = useState(false);
  const [isDNDEnabled, setIsDNDEnabled] = useState(false);
  const isDNDEnabledRef = useRef(false);
  const [dndLoading, setDndLoading] = useState(false);
  const sipInboundMobileOnlyRef = useRef(false);
  const [sipInboundMobileOnlyLoading, setSipInboundMobileOnlyLoading] = useState(false);
  const [screenPopLoading, setScreenPopLoading] = useState(false);
  const activeCallClientId = useMemo(() => {
    // For inbound calls (incomingCall or cold-start pending call), use the
    // call's own client_id — the call belongs to that client regardless of
    // which client the user has selected for outbound.
    const inboundCandidateIds = [
      incomingCall?.client_id,
      coldStartPendingCall?.clientId,
    ];

    for (const candidate of inboundCandidateIds) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    // For outbound / idle state, prefer the selected PABX client from the
    // multi-client selector. Fall back to the JWT active client.
    if (selectedPabxClientId && selectedPabxClientId > 0) {
      return selectedPabxClientId;
    }

    const parsedActive = Number(client?.id);
    if (Number.isFinite(parsedActive) && parsedActive > 0) {
      return parsedActive;
    }

    return 0;
  }, [incomingCall?.client_id, coldStartPendingCall?.clientId, selectedPabxClientId, client?.id]);
  
  // SIP Transfer State
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferType, setTransferType] = useState<'blind' | 'attended'>('blind');
  const [transferringToExtension, setTransferringToExtension] = useState<string | null>(null);
  const [attendedTransferState, setAttendedTransferState] = useState<AttendedTransferState>(
    { status: 'idle' }
  );
  const [attendedTransferSubmitting, setAttendedTransferSubmitting] = useState<'merge' | 'complete' | null>(
    null,
  );
  const [pendingBlindTransfer, setPendingBlindTransfer] = useState<{
    active: boolean;
    targetExtension?: string;
    targetName?: string;
    startedAt?: number;
  }>({ active: false });
  const pendingBlindTransferRef = useRef<boolean>(false);
  // "blind" | "attended" while this user initiated a SIP transfer that is pending.
  const pendingTransferTypeRef = useRef<string | null>(null);
  // True while this session is the *target* of a SIP transfer that hasn't
  // finalized — endCall must not terminate the shared PSTN leg meanwhile.
  const isTransferTargetRef = useRef<boolean>(false);
  
  // Pickup Group State
  const [pickupGroupCalls, setPickupGroupCalls] = useState<PickupGroupCall[]>([]);
  const [showPickupIndicator, setShowPickupIndicator] = useState(false);
  const [pickingUpCall, setPickingUpCall] = useState<string | null>(null);
  
  // Parking Dialog State
  const [showParkingDialog, setShowParkingDialog] = useState(false);
  const [parkingToSlot, setParkingToSlot] = useState<number | null>(null);
  const [parkingSlotRange, setParkingSlotRange] = useState<{ start: number; end: number }>({ start: 701, end: 720 });
  
  const isSipCall = sipCallData !== null || sipCallDataSecondary !== null;
  // Gate-call flag comes via WS (isGateCallIncoming) or the incomingCall prop
  // (parent-driven/cold-start paths).
  const isGateCall = isGateCallIncoming || !!incomingCall?.isGateCall;
  
  // ============================================
  // Tab Logic (must be after callSettings state)
  // ============================================
  const hasSipExtension = !!callSettings?.sip_extension;
  const showKeypad = callModalMode === 'connect-only' || callModalMode === 'pabx-only' || callModalMode === 'full';

  /** CRM phone book + keypad CRM matches — only with an active SIP extension that may place outbound calls. */
  const sipExt = callSettings?.sip_extension;
  const showCrmContactsTab =
    (callModalMode === 'pabx-only' || callModalMode === 'full') &&
    !!sipExt?.extension_number?.trim() &&
    sipExt.can_make_outbound_calls !== false;

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const nextTab = showKeypad ? 'keypad' : 'contacts';
      setContactsSubTab('team');
      setRecentsScope('all');
      setRecentsTimeSort('newest');
      setContactsSort('name_asc');
      setActiveTab(nextTab);
      setViewMode(nextTab as ViewMode);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, showKeypad]);

  useEffect(() => {
    if (activeTab !== 'contacts') {
      setContactsSubTab('team');
    }
  }, [activeTab]);

  useEffect(() => {
    if (!isOpen) {
      setCrmDialerRows([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showCrmContactsTab && contactsSubTab === 'crm') {
      setContactsSubTab('team');
    }
  }, [showCrmContactsTab, contactsSubTab]);

  useEffect(() => {
    if (!isOpen || !showCrmContactsTab || !user?.id) return;
    let cancelled = false;
    void (async () => {
      const rows = await fetchCrmContactsForDialer();
      if (!cancelled) setCrmDialerRows(rows);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, showCrmContactsTab, user?.id]);

  // ============================================
  // Group Call State
  // ============================================
  const [isGroupCall, setIsGroupCall] = useState(false);
  const [groupCallInfo, setGroupCallInfo] = useState<GroupCallInfo | null>(null);
  const [waitingParticipants, setWaitingParticipants] = useState<CallParticipant[]>([]);
  const [isBreakoutActive, setIsBreakoutActive] = useState(false);
  const [breakoutRooms, setBreakoutRooms] = useState<BreakoutRoom[]>([]);
  
  // ============================================
  // Screen Pop State
  // ============================================
  const [screenPopData, setScreenPopData] = useState<ScreenPopData | null>(null);
  
  // ============================================
  // Lock Screen State - tracks if app is showing over device lock screen
  // ============================================
  const showingOverLockScreenRef = useRef(false);
  
  // ============================================
  // Call Queue State - holds incoming calls received while user is busy
  // ============================================
  const [callQueue, setCallQueue] = useState<IncomingCall[]>([]);
  
  // Track the last call ID we handled to prevent re-ringing
  const lastHandledCallIdRef = useRef<string | null>(null);
  const localAnsweringCallIdRef = useRef<string | null>(null);
  // Test call handler (for PABX welcome/echo/hold test calls)
  
  // ============================================
  // UI State
  // ============================================
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(true);
  const [activityLog, setActivityLog] = useState<CallActivityEntry[]>([]);
  const [activeReactions, setActiveReactions] = useState<CallReaction[]>([]);
  const [selectedParticipantMenu, setSelectedParticipantMenu] = useState<string | null>(null);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [whiteboardElements, setWhiteboardElements] = useState<DrawingElement[]>([]);
  /** Who currently has presentation open on the wire (not necessarily us). */
  const [remotePresenterInfo, setRemotePresenterInfo] = useState<{ userId: number; userName: string } | null>(null);
  const remotePresenterInfoRef = useRef(remotePresenterInfo);
  useEffect(() => {
    remotePresenterInfoRef.current = remotePresenterInfo;
  }, [remotePresenterInfo]);

  type WhiteboardUIMode =
    | { kind: 'idle' }
    | { kind: 'presenting' }
    | { kind: 'following'; userId: number; userName: string };

  const [whiteboardUIMode, setWhiteboardUIMode] = useState<WhiteboardUIMode>({ kind: 'idle' });
  const whiteboardUIModeRef = useRef<WhiteboardUIMode>(whiteboardUIMode);
  useEffect(() => {
    whiteboardUIModeRef.current = whiteboardUIMode;
  }, [whiteboardUIMode]);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<Date | null>(null);
  const [activeRecordingId, setActiveRecordingId] = useState<string | null>(null);
  const [showBreakoutModal, setShowBreakoutModal] = useState(false);
  const [currentBreakoutRoom, setCurrentBreakoutRoom] = useState<string | null>(null);
  const [addingParticipant, setAddingParticipant] = useState<number | null>(null);
  const [showInviteLink, setShowInviteLink] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [participantHandRaises, setParticipantHandRaises] = useState<Map<number, boolean>>(new Map());
  const [remoteVideoActive, setRemoteVideoActive] = useState(false);
  const [mediaAudioBlocked, setMediaAudioBlocked] = useState(false);
  const [showCallSettings, setShowCallSettings] = useState(false);
  const [showPhoneContacts, setShowPhoneContacts] = useState(false);
  const [showVoicemail, setShowVoicemail] = useState(false);
  const browserAudioPrimedRef = useRef(false);
  
  // ============================================
  // Device Switching State
  // ============================================
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [transferringTo, setTransferringTo] = useState<string | null>(null);
  
  // Fetch active sessions for device switching during calls
  const fetchActiveSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) { setLoadingSessions(false); return; }
      const response = await fetch(buildApiUrl('/api/v1/sessions/active'), {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });
      const data = await response.json();
      if (data.success) {
        setActiveSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error fetching active sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  // Fetch active sessions when call connects (for device switcher)
  useEffect(() => {
    if (callState === 'connected') {
      fetchActiveSessions();
    }
  }, [callState, fetchActiveSessions]);

  const primeBrowserCallAudio = useCallback(async () => {
    if (
      typeof window === 'undefined'
      || isCapacitor()
      || browserAudioPrimedRef.current
    ) {
      return;
    }

    try {
      const AudioContextCtor = window.AudioContext || (window as Window & typeof globalThis & {
        webkitAudioContext?: typeof AudioContext;
      }).webkitAudioContext;

      if (!AudioContextCtor) {
        browserAudioPrimedRef.current = true;
        return;
      }

      const audioContext = new AudioContextCtor();

      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      gain.gain.setValueAtTime(0.00001, audioContext.currentTime);
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.02);

      browserAudioPrimedRef.current = true;
      window.setTimeout(() => {
        void audioContext.close().catch(() => {});
      }, 200);
    } catch (error) {
      console.warn('[TotalConnectRTC] Browser audio priming skipped:', error);
    }
  }, []);
  
  // ============================================
  // Meeting Settings
  // ============================================
  const [meetingSettingsConfig, setMeetingSettingsConfig] = useState<MeetingSettings>({
    allowWhiteboard: true,
    allowRecording: true,
    enableLobby: false,
    autoAdmit: true,
    muteOnEntry: false,
    disableVideo: false,
  });

  // ============================================
  // Quick meeting (create + schedule) state
  // ============================================
  const [creatingInstantMeeting, setCreatingInstantMeeting] = useState(false);
  const [showScheduleMeetingModal, setShowScheduleMeetingModal] = useState(false);
  const [callRoomUrl, setCallRoomUrl] = useState<string | null>(null);
  const [callRoomCallId, setCallRoomCallId] = useState<string | null>(null);
  const [callRoomInstanceId, setCallRoomInstanceId] = useState<string | null>(null);
  const {
    teams: calendarTeams,
    events: calendarEvents,
    fetchTeams,
    createEvent,
    fetchEvents,
  } = useCalendarStore();

  // Fetch teams when the schedule modal opens (same as EmbeddedCalls)
  useEffect(() => {
    if (showScheduleMeetingModal) {
      fetchTeams();
    }
  }, [showScheduleMeetingModal, fetchTeams]);

  // Load calendar events when the Meetings tab is shown so the upcoming list
  // is fresh (same 30-day window EmbeddedCalls uses after scheduling).
  useEffect(() => {
    if (isOpen && activeTab === 'meetings') {
      fetchEvents(new Date(), addHours(new Date(), 24 * 30));
    }
  }, [isOpen, activeTab, fetchEvents]);

  const upcomingMeetingEvents = useMemo(() => {
    const now = Date.now();
    return (calendarEvents || [])
      .filter((event) => {
        const end = event.end instanceof Date ? event.end : new Date(event.end);
        return (event.isVideoMeeting || !!event.meetingUrl) && end.getTime() > now;
      })
      .sort(
        (a, b) =>
          new Date(a.start).getTime() - new Date(b.start).getTime(),
      )
      .slice(0, 4);
  }, [calendarEvents]);

  // Close the in-window meeting room when the call inside it ends. The room
  // echoes the instanceId we gave it — a different /call/room document for the
  // same callId (another tab, stale page) must not close our live iframe.
  useEffect(() => {
    if (!callRoomCallId) return;
    const channel = new BroadcastChannel(`call-${callRoomCallId}`);
    channel.onmessage = (event) => {
      if (
        event.data?.type === 'CALL_ENDED' &&
        event.data?.data?.instanceId === callRoomInstanceId
      ) {
        setCallRoomUrl(null);
        setCallRoomCallId(null);
        setCallRoomInstanceId(null);
      }
    };
    return () => channel.close();
  }, [callRoomCallId, callRoomInstanceId]);

  const closeCallRoomModal = useCallback(() => {
    // Tell the room to hang up gracefully (it broadcasts CALL_ENDED back to us)
    if (callRoomCallId) {
      sendCallPopupCommand(callRoomCallId, 'END_CALL', callRoomInstanceId ?? undefined);
      // Fallback in case the room is not yet connected / never broadcasts back
      setTimeout(() => {
        setCallRoomUrl(null);
        setCallRoomCallId(null);
        setCallRoomInstanceId(null);
      }, 1500);
    } else {
      setCallRoomUrl(null);
    }
  }, [callRoomCallId, callRoomInstanceId]);

  const requestCloseCallRoomModal = useCallback(() => {
    void notify.confirm({
      title: 'Leave meeting?',
      message: 'This will disconnect you from the meeting.',
      confirmText: 'Leave meeting',
      cancelText: 'Stay',
      variant: 'warning',
      onConfirm: () => closeCallRoomModal(),
    });
  }, [closeCallRoomModal]);

  // While the meeting overlay is open, the hardware/in-app back button should
  // close it (with confirmation) — never let a single press drop to dashboard
  // or minimize the app mid-meeting.
  useEffect(() => {
    if (!callRoomUrl) return;
    registerBackHandler('call-room-modal', requestCloseCallRoomModal);
    return () => unregisterBackHandler('call-room-modal');
  }, [callRoomUrl, registerBackHandler, unregisterBackHandler, requestCloseCallRoomModal]);

  // Same flow as EmbeddedCalls.startMeetingAsHost — create the meeting room via
  // the initiate API (no targets = meeting room), then open /call/room full-screen.
  const startInstantMeeting = useCallback(async () => {
    if (creatingInstantMeeting) return;
    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      toast.error('Please sign in to start a meeting');
      return;
    }
    setCreatingInstantMeeting(true);
    try {
      const chars = 'abcdefghijklmnopqrstuvwxyz';
      const rand = (n: number) =>
        Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
      const displayId = `${rand(3)}-${rand(4)}-${rand(3)}`;

      const response = await fetch('/api/v1/calls', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'initiate',
          target_user_ids: [], // Empty = create meeting room only
          call_type: 'video',
          title: 'Meeting Room',
          is_meeting_room: true,
          display_id: displayId,
          settings: {
            waiting_room_enabled: meetingSettingsConfig.enableLobby,
            auto_admit_guests: meetingSettingsConfig.autoAdmit,
            default_video_on: !meetingSettingsConfig.disableVideo,
            default_mute_on_join: meetingSettingsConfig.muteOnEntry,
          },
        }),
      });
      const data = await response.json();
      const meetingId = data?.call?.meeting_id;
      const livekitToken = data?.call?.livekit_token;
      if (!data.success || !meetingId || !livekitToken) {
        throw new Error(data?.error || 'Failed to create meeting');
      }

      const callerName = user?.first_name
        ? `${user.first_name} ${user.last_name || ''}`.trim()
        : user?.email || 'Host';
      const instanceId = createCallInstanceId();
      const callParams = {
        callId: meetingId,
        token: livekitToken,
        host: getLiveKitUrl(),
        type: 'video' as const,
        callerName,
        targetName: 'Meeting Room',
        isMeetingRoom: true,
        instanceId,
      };

      if (isTauri()) {
        openCallPopup(callParams);
        return;
      }

      const url = prepareCallRoomUrl(callParams);
      // Preflight camera/mic permission in this window so the iframe captures silently.
      void preflightMediaPermissions(true).finally(() => {
        setCallRoomCallId(meetingId);
        setCallRoomInstanceId(instanceId);
        setCallRoomUrl(url);
      });
    } catch (err: unknown) {
      console.error('[TotalConnectRTC] Failed to start instant meeting:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to start meeting');
    } finally {
      setCreatingInstantMeeting(false);
    }
  }, [creatingInstantMeeting, user, meetingSettingsConfig]);

  // Same save path as EmbeddedCalls.handleCalendarModalSave
  const handleScheduleMeetingSave = useCallback(
    async (
      eventData: Partial<CalendarEvent> & { team_id?: string; is_team_event?: boolean },
      options?: { sendInvites?: boolean; customMessage?: string }
    ) => {
      if (eventData.title && eventData.start && eventData.end) {
        if (options?.sendInvites && eventData.attendees && eventData.attendees.length > 0) {
          const { inviteResult } = await useCalendarStore.getState().createEventWithInvites(
            {
              title: eventData.title,
              start: eventData.start,
              end: eventData.end,
              allDay: eventData.allDay,
              location: eventData.location,
              description: eventData.description,
              color: eventData.color,
              isVideoMeeting: eventData.isVideoMeeting,
              attendees: eventData.attendees,
              teamId: eventData.team_id,
              isTeamEvent: eventData.is_team_event,
              meetingUrl: undefined,
            },
            {
              sendInvites: true,
              customMessage: options.customMessage,
            }
          );
          if (inviteResult) {
            const sentCount = inviteResult.sent.length;
            const failedCount = inviteResult.failed.length;
            if (failedCount > 0) {
              toast.error(
                `Event created, but ${failedCount} invite${failedCount !== 1 ? 's' : ''} failed to send: ${inviteResult.failed[0].error}${failedCount > 1 ? ' (…)' : ''}`
              );
            } else if (sentCount > 0) {
              toast.success(`Meeting invite${sentCount !== 1 ? 's' : ''} sent to ${sentCount} attendee${sentCount !== 1 ? 's' : ''}`);
            }
          }
        } else {
          await createEvent({
            title: eventData.title,
            start: eventData.start,
            end: eventData.end,
            allDay: eventData.allDay,
            location: eventData.location,
            description: eventData.description,
            color: eventData.color,
            isVideoMeeting: eventData.isVideoMeeting,
            attendees: eventData.attendees,
            team_id: eventData.team_id,
            is_team_event: eventData.is_team_event,
          });
        }
      }
      setShowScheduleMeetingModal(false);
      fetchEvents(new Date(), addHours(new Date(), 24 * 30));
    },
    [createEvent, fetchEvents]
  );
  
  // ============================================
  // Refs
  // ============================================
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const roomRef = useRef<Room | null>(null);
  const localTracksRef = useRef<LocalTrack[]>([]);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const ringTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callIdRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<number | null>(0);

  const roomRefSecondary = useRef<Room | null>(null);
  const localTracksRefSecondary = useRef<LocalTrack[]>([]);
  const callIdRefSecondary = useRef<string | null>(null);
  const secondaryElapsedStartRef = useRef<number | null>(null);
  const focusedRoomRef = useRef<Room | null>(null);
  const sipHoldLegRef = useRef<{
    call_log_id?: string;
    livekit_room?: string;
    client_id?: number;
    callId: string | null;
  } | null>(null);
  const secondaryLineOccupiedRef = useRef(false);
  const secondLinePendingRef = useRef(false);
  const presentSecondLineIncomingRef = useRef<
    ((call: IncomingCall) => void) | null
  >(null);

  const wsRef = useRef<WebSocket | null>(null);

  const sipHoldMusicRef = useRef<((hold: boolean) => Promise<unknown>) | null>(
    null,
  );

  const sendWhiteboardStatus = useCallback((is_open: boolean) => {
    if (!callIdRef.current || user?.id == null) return;
    const strip = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    wsClient.send({
      type: 'call_whiteboard_status',
      call_id: callIdRef.current,
      user_id: user.id,
      user_name: strip || user?.email || 'Participant',
      is_open,
    });
  }, [user]);

  const toggleWhiteboard = useCallback(
    (open: boolean) => {
      const myId = user?.id;

      if (open) {
        const remote = remotePresenterInfoRef.current;
        const othersBoardOpen =
          remote != null && myId != null && remote.userId !== myId;

        if (othersBoardOpen) {
          setWhiteboardUIMode({
            kind: 'following',
            userId: remote.userId,
            userName: remote.userName,
          });
          setShowWhiteboard(true);
          return;
        }

        setWhiteboardUIMode({ kind: 'presenting' });
        setShowWhiteboard(true);
        sendWhiteboardStatus(true);
        return;
      }

      const mode = whiteboardUIModeRef.current;
      if (mode.kind === 'presenting') sendWhiteboardStatus(false);
      setWhiteboardUIMode({ kind: 'idle' });
      setShowWhiteboard(false);
    },
    [user?.id, sendWhiteboardStatus]
  );
  
  // SIP-specific refs
  const isSipCallRef = useRef(false);
  const sipCallLogIdRef = useRef<string | null>(null);
  const pendingAnswerMessageRef = useRef<AnswerSignalPayload | null>(null);
  const pendingConnectionRef = useRef<{
    host: string;
    token: string;
    type: CallType;
  } | null>(null);
  
  // Refs for callback props (to avoid WebSocket reconnections)
  const onIncomingCallRef = useRef(onIncomingCall);
  const onClearIncomingCallRef = useRef(onClearIncomingCall);
  const onMissedCallsChangeRef = useRef(onMissedCallsChange);
  const missedCallsCountRef = useRef(missedCallsCount);
  
  // Keep refs in sync
  useEffect(() => { onIncomingCallRef.current = onIncomingCall; }, [onIncomingCall]);
  useEffect(() => { onClearIncomingCallRef.current = onClearIncomingCall; }, [onClearIncomingCall]);
  useEffect(() => { onMissedCallsChangeRef.current = onMissedCallsChange; }, [onMissedCallsChange]);
  useEffect(() => { missedCallsCountRef.current = missedCallsCount; }, [missedCallsCount]);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { isDNDEnabledRef.current = isDNDEnabled; }, [isDNDEnabled]);
  useEffect(() => { sipCallDataRef.current = sipCallData; }, [sipCallData]);
  useEffect(() => { callStateRef.current = callState; }, [callState]);

  const markColdStartCallHandling = useCallback((callId: string | null | undefined) => {
    if (typeof window === 'undefined' || !callId) return;

    try {
      localStorage.setItem('coldstart_call_handling', JSON.stringify({
        callId,
        timestamp: Date.now(),
      }));
    } catch (_e) {
      // Ignore storage issues during cold start handling
    }
  }, []);

  const clearColdStartCallHandling = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem('coldstart_call_handling');
    } catch (_e) {
      // Ignore storage issues during cleanup
    }
  }, []);
  
  // ============================================
  // SIP Detection from incomingCall Prop (FCM / Cold Start Path)
  // When calls arrive via FCM notification (app in background), the WebSocket handler
  // never fires, so sipCallData is never set. Detect SIP from the incomingCall prop
  // and set sipCallData so the UI shows the correct SIP buttons (Hold/Transfer/Park/Keypad).
  // ============================================
  useEffect(() => {
    if (!incomingCall) return;
    // Skip if sipCallData already set (WebSocket handler already processed it)
    if (sipCallData !== null) return;
    
    const normalizedIncomingCall: LegacyIncomingCall = incomingCall;
    const callId = normalizedIncomingCall.call_id || normalizedIncomingCall.callId;
    const isSipFromProp =
      normalizedIncomingCall.isSipCall || callId?.startsWith('sip-inbound-');
    
    if (isSipFromProp) {
      const sipData: SipCallData = normalizedIncomingCall.sipData ?? {};
      setSipCallData({
        ...sipData,
        call_log_id: sipData.call_log_id,
        client_id: normalizedIncomingCall.client_id || sipData.client_id,
        livekit_room: sipData.livekit_room || callId,
        livekit_token: sipData.livekit_token,
        livekit_host: sipData.livekit_host,
        caller_number:
          normalizedIncomingCall.caller_number || sipData.caller_number,
        caller_name: normalizedIncomingCall.caller_name || sipData.caller_name,
        trunk_id: sipData.trunk_id,
        trunk_name: sipData.trunk_name,
        did_number: sipData.did_number,
        target_extension: sipData.target_extension,
        extension_display_name: sipData.extension_display_name,
        ring_group_id: sipData.ring_group_id,
        ring_group_name: sipData.ring_group_name,
        ring_strategy: sipData.ring_strategy,
        ring_timeout: sipData.ring_timeout,
        queue_id: sipData.queue_id,
        queue_name: sipData.queue_name,
        queue_position: sipData.queue_position,
        test_mode: sipData.test_mode,
        user_display_name: sipData.user_display_name,
      });
      isSipCallRef.current = true;
      sipCallLogIdRef.current = sipData.call_log_id || null;
    }
  }, [incomingCall, sipCallData]);
  
  // ============================================
  // Lock Screen Management for Capacitor
  // Show call UI over lock screen on incoming calls, return to lock on end
  // ============================================
  useEffect(() => {
    if (!isCapacitor()) return;
    
    if (callState === 'ringing' || callState === 'connecting' || callState === 'connected') {
      // Enable showing over lock screen so user can answer calls without unlocking
      LockScreen.enableShowOverLockScreen().then(result => {
        if (result.success) {
          showingOverLockScreenRef.current = true;
        }
      }).catch(e => console.warn('[TotalConnectRTC] Lock screen enable failed:', e));
    } else if (callState === 'idle' || callState === 'ended') {
      // Call ended — if we were showing over lock screen, return to lock
      if (showingOverLockScreenRef.current) {
        showingOverLockScreenRef.current = false;
        LockScreen.isDeviceLocked().then(state => {
          if (state.isLocked) {
            LockScreen.finishAndLock().catch(e => 
              console.warn('[TotalConnectRTC] finishAndLock failed:', e)
            );
          }
        }).catch(e => console.warn('[TotalConnectRTC] Lock state check failed:', e));
      }
    }
  }, [callState]);

  // On Capacitor, show main Header when modal opens
  useEffect(() => {
    if (isOpen && isCapacitor()) {
      window.dispatchEvent(new Event('showHeader'));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isTauriDesktop) {
      return;
    }

    const shouldEnableCallMode =
      callState === 'ringing'
      || callState === 'initiating'
      || callState === 'connecting'
      || callState === 'connected'
      || callState === 'reconnecting';

    if (shouldEnableCallMode) {
      void enableCallMode();
      return;
    }

    void disableCallMode();
  }, [callState, disableCallMode, enableCallMode, isTauriDesktop]);

  useEffect(() => {
    if (
      callState === 'idle'
      || callState === 'ringing'
      || callState === 'initiating'
      || callState === 'connecting'
    ) {
      setMediaAudioBlocked(false);
    }
  }, [callState]);
  
  // ============================================
  // Activity Log Helper
  // ============================================
  const addActivityEntry = useCallback((activityType: CallActivityEntry['type'], participantName: string, participantId?: number) => {
    setActivityLog(prev => [...prev.slice(-49), {
      id: `activity-${Date.now()}`,
      timestamp: new Date(),
      type: activityType,
      participantName,
      participantId,
    }]);
  }, []);
  
  // ============================================
  // Notifications Hook Ref
  // ============================================
  const callNotificationsRef = useRef<ReturnType<typeof useCallNotifications> | null>(null);
  
  // Ref to hold callControls.setVideoEnabled so the onVideoPublished callback
  // in useCallSession can call it without a circular init dependency
  const setVideoEnabledRef = useRef<((enabled: boolean) => void) | null>(null);

  // Mirror of callControls.isSpeakerOn so useCallSession can re-apply the
  // user's audio route on remote track attach (circular init dependency)
  const speakerPreferenceRef = useRef(false);
  
  // ============================================
  // Initialize Hooks
  // ============================================
  const callSession = useCallSession({
    roomRef,
    localTracksRef,
    callIdRef,
    userId: user?.id || 0,
    userName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    userAvatar: user?.avatar_url,
    onVideoPublished: (videoEnabled) => {
      setVideoEnabledRef.current?.(videoEnabled);
    },
    getSpeakerPreference: () => speakerPreferenceRef.current,
    onParticipantJoined: (participant) => {
      addActivityEntry('join', participant.name, participant.userId);

      // SIP legs join the room while the callee is still ringing — only treat
      // the join as "answered" once sip.callStatus reports active/automation.
      if (
        callStateRef.current === 'initiating' &&
        isSipParticipantIdentity(participant.identity) &&
        isSipLegStillRinging(participant.attributes)
      ) {
        setOutgoingCallProgress('ringing');
        return;
      }

      // When a remote participant joins and we're the caller waiting...
      // This means the callee has answered and joined the room
      if (callStateRef.current === 'connecting' || callStateRef.current === 'initiating') {
        callNotificationsRef.current?.stopDialTone();
        setOutgoingCallProgress('idle');
        setCallState('connected');
        callStateRef.current = 'connected';
        callStartTimeRef.current = Date.now();
      }
    },
    onParticipantLeft: (identity) => {
      // An outbound SIP leg dropping while still ringing means the callee
      // never answered (busy, rejected, unreachable) — fail fast instead of
      // waiting for the 50s no-answer timeout.
      if (
        callStateRef.current === 'initiating' &&
        sipCallDataRef.current &&
        isSipParticipantIdentity(identity)
      ) {
        void endCallRef.current?.('unanswered');
        return;
      }

      // When remote participant leaves during a 1:1 call, end the call —
      // BUT only if no other remote participants remain (e.g. after a transfer,
      // the transferor leaves but the SIP participant stays).
      if (callStateRef.current === 'connected' && !isGroupCall) {
        const remainingRemotes = roomRef.current
          ? Array.from(roomRef.current.remoteParticipants.values()).length
          : 0;
        
        if (remainingRemotes > 0) {
          return;
        }
        
        callNotificationsRef.current?.stopRingtone();
        callNotificationsRef.current?.stopDialTone();
        
        // Clean up server-side participant record via REST API
        const currentCallId = callIdRef.current;
        if (currentCallId) {
          lastHandledCallIdRef.current = currentCallId;
          const tokens = authService.getTokens();
          fetch(buildApiUrl('/api/v1/calls'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${tokens?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'end', call_id: currentCallId, reason: 'remote_hangup' }),
          }).catch(err => console.error('[TotalConnectRTC] Failed to clean up call record:', err));
        }
        
        setCallState('idle');
        callStateRef.current = 'idle';
        setViewMode('contacts');
        setCallDuration(0);
        setRemoteVideoActive(false);
        callIdRef.current = null;
        callStartTimeRef.current = null;
        onClearIncomingCallRef.current?.();
        callSession.disconnectFromRoom('remote_hangup');
      }
    },
    onTrackSubscribed: (track, participant) => {
      if (track.kind === Track.Kind.Video) {
        setRemoteVideoActive(true);
        const videoEl = remoteVideoRefs.current.get(participant.identity);
        if (videoEl) {
          track.attach(videoEl);
        }
      }
    },
    onTrackUnsubscribed: (track, participant) => {
      if (track.kind === Track.Kind.Video) {
        queueMicrotask(() => {
          const room = roomRef.current;
          if (!room) {
            setRemoteVideoActive(false);
            return;
          }
          let anyRemoteVideo = false;
          for (const rp of room.remoteParticipants.values()) {
            for (const pub of rp.videoTrackPublications.values()) {
              if (pub.isSubscribed && pub.track) {
                anyRemoteVideo = true;
                break;
              }
            }
            if (anyRemoteVideo) break;
          }
          setRemoteVideoActive(anyRemoteVideo);
        });
      }
    },
    onConnectionStateChange: (state) => {
      if (state === ConnectionState.Reconnecting) {
        setCallState('reconnecting');
      }
    },
    onDisconnected: (reason) => {
      callNotificationsRef.current?.stopRingtone();
      callNotificationsRef.current?.stopDialTone();
      const secondaryLive =
        roomRefSecondary.current?.state === ConnectionState.Connected;
      if (secondaryLive) {
        setSipCallData(null);
        setScreenPopData(null);
        callIdRef.current = null;
        setFocusedLine('secondary');
        setCallState('connected');
        callStateRef.current = 'connected';
        setSecondLineIncoming(null);
        void roomRefSecondary.current?.localParticipant
          .setMicrophoneEnabled(true)
          .catch(() => {});
        return;
      }
      setCallState('idle');
      setViewMode('contacts');
      setCallDuration(0);
      setSelectedUser(null);
      setSipCallData(null);
      setScreenPopData(null);
      setIsGroupCall(false);
      setGroupCallInfo(null);
      callIdRef.current = null;
      callStartTimeRef.current = null;
      onClearIncomingCallRef.current?.();
    },
    onAudioBlocked: () => {
      setMediaAudioBlocked(true);
    },
  });

  const callSessionSecondary = useCallSession({
    roomRef: roomRefSecondary,
    localTracksRef: localTracksRefSecondary,
    callIdRef: callIdRefSecondary,
    userId: user?.id || 0,
    userName: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    userAvatar: user?.avatar_url,
    getSpeakerPreference: () => speakerPreferenceRef.current,
    onDisconnected: () => {
      callNotificationsRef.current?.stopRingtone();
      setSecondLineIncoming(null);
      setSipCallDataSecondary(null);
      setSelectedUserSecondary(null);
      setScreenPopDataSecondary(null);
      callIdRefSecondary.current = null;
      secondaryElapsedStartRef.current = null;
      secondaryLineOccupiedRef.current = false;
      const primaryLive =
        roomRef.current?.state === ConnectionState.Connected;
      if (primaryLive) {
        setFocusedLine('primary');
        void sipHoldMusicRef.current?.(false);
        void roomRef.current?.localParticipant
          .setMicrophoneEnabled(true)
          .catch(() => {});
        toast.success('Second line ended');
        return;
      }
      setCallState('idle');
      callStateRef.current = 'idle';
      setViewMode('contacts');
      setCallDuration(0);
      onClearIncomingCallRef.current?.();
    },
    onAudioBlocked: () => {
      setMediaAudioBlocked(true);
    },
  });

  presentSecondLineIncomingRef.current = (call: IncomingCall) => {
    setSecondLineIncoming((prev) =>
      prev?.call_id === call.call_id ? prev : call,
    );
  };

  secondaryLineOccupiedRef.current =
    roomRefSecondary.current?.state === ConnectionState.Connected;

  secondLinePendingRef.current = secondLineIncoming !== null;

  focusedRoomRef.current =
    focusedLine === 'primary' ? roomRef.current : roomRefSecondary.current;

  sipHoldLegRef.current =
    focusedLine === 'primary'
      ? {
          call_log_id: sipCallData?.call_log_id,
          livekit_room: sipCallData?.livekit_room,
          client_id: sipCallData?.client_id,
          callId: callIdRef.current,
        }
      : {
          call_log_id: sipCallDataSecondary?.call_log_id,
          livekit_room: sipCallDataSecondary?.livekit_room,
          client_id: sipCallDataSecondary?.client_id,
          callId: callIdRefSecondary.current,
        };

  const callControls = useCallControls({
    roomRef: focusedRoomRef,
    wsRef,
    callIdRef,
    sipCallData,
    sipHoldLegRef,
    user: user ? { id: user.id, first_name: user.first_name, last_name: user.last_name } : null,
  });
  
  const callNotifications = useCallNotifications();

  const clearQueuedOutgoingRingingTransition = useCallback(() => {
    if (queuedOutgoingRingingTimeoutRef.current) {
      clearTimeout(queuedOutgoingRingingTimeoutRef.current);
      queuedOutgoingRingingTimeoutRef.current = null;
    }
  }, []);

  const setOutgoingProgressState = useCallback(
    (next: OutgoingCallProgress) => {
      clearQueuedOutgoingRingingTransition();
      setOutgoingCallProgress(next);

      if (next === 'ringing') {
        callNotifications.playDialTone();
        return;
      }

      callNotifications.stopDialTone();
    },
    [callNotifications, clearQueuedOutgoingRingingTransition],
  );

  const resetOutgoingProgressState = useCallback(
    (stopTone: boolean = true) => {
      clearQueuedOutgoingRingingTransition();
      outgoingInternalCallRef.current = false;
      setOutgoingCallProgress('idle');
      if (stopTone) {
        callNotifications.stopDialTone();
      }
    },
    [callNotifications, clearQueuedOutgoingRingingTransition],
  );
  
  // Store notifications ref for use in callbacks
  useEffect(() => {
    callNotificationsRef.current = callNotifications;
  }, [callNotifications]);

  // Keep speaker preference ref in sync for useCallSession's route re-apply
  useEffect(() => {
    speakerPreferenceRef.current = callControls.isSpeakerOn;
  }, [callControls.isSpeakerOn]);

  useEffect(() => {
    if (!outgoingInternalCallRef.current) {
      return;
    }

    if (callState === 'connecting') {
      setOutgoingProgressState('connecting');
      return;
    }

    if (callState === 'connected' || callState === 'idle' || callState === 'ringing' || callState === 'ended') {
      resetOutgoingProgressState();
    }
  }, [callState, resetOutgoingProgressState, setOutgoingProgressState]);
  
  // Sync the setVideoEnabled ref so onVideoPublished can call it
  useEffect(() => {
    setVideoEnabledRef.current = callControls.setVideoEnabled || null;
  }, [callControls.setVideoEnabled]);
  
  const callPlugins = useCallPlugins({
    isInCall: callState === 'connected' || callState === 'connecting' || callState === 'reconnecting',
    isVideoCall: callType === 'video',
  });
  
  // Ref for busy detection in WebSocket handler
  const checkIfBusyRef = useRef(callPlugins.checkIfBusy);
  useEffect(() => { checkIfBusyRef.current = callPlugins.checkIfBusy; }, [callPlugins.checkIfBusy]);
  
  // ============================================
  // Online Status Helper (matches MessagesModalNew logic)
  // ============================================
  const isUserOnline = useCallback((userOrId: OperationsUser | number) => {
    const u = typeof userOrId === 'number' 
      ? operationsUsers.find(ou => ou.id === userOrId)
      : userOrId;
    
    if (!u) return false;
    if (u.show_when_online === false) return false; // Respect privacy preference
    if (!u.last_activity_at) return false;
    
    const lastActive = new Date(u.last_activity_at).getTime();
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    return lastActive > fiveMinutesAgo;
  }, [operationsUsers]);
  
  const getUserLastSeen = useCallback((userOrId: OperationsUser | number) => {
    const u = typeof userOrId === 'number' 
      ? operationsUsers.find(ou => ou.id === userOrId)
      : userOrId;
    
    if (!u) return null;
    if (u.show_when_online === false) return null; // Respect privacy preference
    if (u.has_active_session === false) return 'Offline';
    if (!u.last_activity_at) {
      return u.has_active_session ? 'Available' : null;
    }
    
    try {
      const lastActive = new Date(u.last_activity_at);
      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60));
      
      if (diffMinutes <= 5) return 'Online';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
      
      const sastTime = lastActive.toLocaleString('en-ZA', { 
        timeZone: 'Africa/Johannesburg',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      return sastTime;
    } catch {
      return null;
    }
  }, [operationsUsers]);

  /** Softphone-style lane list: dual LiveKit SIP legs plus consultation / transfer-ringing. */
  const activeCallLines = useMemo((): ActiveCallLine[] => {
    const railEligible =
      isSipCall &&
      (callState === 'connected' ||
        callState === 'connecting' ||
        callState === 'reconnecting');
    if (!railEligible) return [];

    const primaryLabel =
      screenPopData?.contact?.company_name ||
      screenPopData?.contact?.contact_person ||
      `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`.trim() ||
      'Line 1';

    const primaryConnected = !!sipCallData && callSession.isConnected;
    const secondaryConnected =
      !!sipCallDataSecondary && callSessionSecondary.isConnected;

    const lines: ActiveCallLine[] = [];

    if (primaryConnected) {
      const primaryState: ActiveCallLine['state'] =
        focusedLine === 'primary'
          ? callControls.isOnHold
            ? 'held'
            : 'active'
          : 'held';
      lines.push({
        id: 'primary',
        label: primaryLabel,
        subtitle:
          sipCallData?.caller_number || sipCallData?.did_number || undefined,
        state: primaryState,
        isFocused: focusedLine === 'primary',
      });
    }

    if (secondLineIncoming?.call_id && !secondaryConnected) {
      lines.push({
        id: 'second-line-incoming',
        label:
          secondLineIncoming.caller_name?.trim() ||
          secondLineIncoming.caller_number ||
          'Incoming call',
        subtitle:
          secondLineIncoming.caller_number &&
          secondLineIncoming.caller_name?.trim()
            ? secondLineIncoming.caller_number
            : 'Call waiting — use banner to answer',
        state: 'ringing',
        selectable: false,
      });
    }

    if (secondaryConnected) {
      const secondaryLabel =
        screenPopDataSecondary?.contact?.company_name ||
        `${selectedUserSecondary?.first_name || ''} ${selectedUserSecondary?.last_name || ''}`.trim() ||
        'Line 2';
      const secondaryState: ActiveCallLine['state'] =
        focusedLine === 'secondary'
          ? callControls.isOnHold
            ? 'held'
            : 'active'
          : 'held';
      lines.push({
        id: 'secondary',
        label: secondaryLabel,
        subtitle: sipCallDataSecondary?.caller_number || undefined,
        state: secondaryState,
        isFocused: focusedLine === 'secondary',
      });
    }

    if (
      attendedTransferState.status === 'consulting' &&
      attendedTransferState.targetName
    ) {
      lines.push({
        id: 'consult',
        label: attendedTransferState.targetName,
        subtitle: 'Consultation',
        state: 'consultation',
      });
    }
    if (pendingBlindTransfer.active && pendingBlindTransfer.targetName) {
      lines.push({
        id: 'xfer-ring',
        label: pendingBlindTransfer.targetName,
        subtitle: 'Transfer ringing',
        state: 'ringing',
      });
    }
    return lines;
  }, [
    isSipCall,
    callState,
    sipCallData,
    sipCallDataSecondary,
    screenPopData?.contact?.company_name,
    screenPopData?.contact?.contact_person,
    screenPopDataSecondary?.contact?.company_name,
    selectedUser?.first_name,
    selectedUser?.last_name,
    selectedUserSecondary?.first_name,
    selectedUserSecondary?.last_name,
    callSession.isConnected,
    callSessionSecondary.isConnected,
    callControls.isOnHold,
    focusedLine,
    attendedTransferState.status,
    attendedTransferState.targetName,
    pendingBlindTransfer.active,
    pendingBlindTransfer.targetName,
    secondLineIncoming?.call_id,
    secondLineIncoming?.caller_name,
    secondLineIncoming?.caller_number,
  ]);
  
  // ============================================
  // Data Fetching
  // ============================================
  const fetchOperationsUsers = useCallback(async () => {
    if (!user) return;
    setLoadingUsers(true);
    try {
      const tokens = authService.getTokens();
      // Pass the selected PABX client_id so contacts are fetched for the
      // client the user has selected in the multi-client dropdown. Falls back
      // to the JWT active client when no selection is made.
      const params = new URLSearchParams({
        module_key: 'total-connect',
        include_partners: 'true',
      });
      if (selectedPabxClientId && selectedPabxClientId > 0) {
        params.set('client_id', String(selectedPabxClientId));
      }
      const res = await fetch(buildApiUrl(`/api/v1/module-access/users?${params.toString()}`), {
        headers: { 'Authorization': `Bearer ${tokens?.access_token}` },
      });
      const data = await res.json();
      if (data.success) {
        // Filter out the current user from contacts
        const filteredUsers = (data.data || []).filter((u: OperationsUser) => u.id !== user?.id);
        setOperationsUsers(filteredUsers);
      }
    } catch (e) {
      console.error('[TotalConnectRTC] Failed to fetch users:', e);
    } finally {
      setLoadingUsers(false);
    }
  }, [user, selectedPabxClientId]);
  
  // Refs for enrichment data so fetchCallLogs doesn't depend on them
  // (avoids infinite re-render loop when arrays change identity)
  const extensionListRef = useRef<SipExtension[]>([]);
  extensionListRef.current = extensionList;
  const operationsUsersRef = useRef<OperationsUser[]>([]);
  operationsUsersRef.current = operationsUsers;
  // Refs for multi-client selection so fetchCallLogs/markMissedCallsAsViewed
  // can read the latest values without becoming stale closures.
  const recentsAllClientsRef = useRef(false);
  useEffect(() => { recentsAllClientsRef.current = recentsAllClients; }, [recentsAllClients]);
  const selectedPabxClientIdRef = useRef<number | null>(null);
  useEffect(() => { selectedPabxClientIdRef.current = selectedPabxClientId; }, [selectedPabxClientId]);

  const fetchCallLogs = useCallback(async () => {
    if (!user) return;
    setLoadingCallLogs(true);
    try {
      const tokens = authService.getTokens();
      // Build query params for multi-client call history.
      // - When recentsAllClients is true, fetch merged history across all accessible clients.
      // - Otherwise, fetch for the selected PABX client (or active client as fallback).
      const params = new URLSearchParams();
      const allClients = recentsAllClientsRef.current;
      if (allClients) {
        params.set('all_clients', 'true');
      } else if (selectedPabxClientIdRef.current && selectedPabxClientIdRef.current > 0) {
        params.set('client_ids', String(selectedPabxClientIdRef.current));
      }
      const queryString = params.toString();
      const url = queryString
        ? buildApiUrl(`/api/v1/operations/call-logs?${queryString}`)
        : buildApiUrl('/api/v1/operations/call-logs');
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${tokens?.access_token}` },
      });
      const data = await res.json();
      if (data.success) {
        // Enrich call logs: resolve extension numbers to user names
        const exts = extensionListRef.current;
        const users = operationsUsersRef.current;
        const enrichedLogs = (data.data || []).map((log: CallLog) => {
          // For SIP outbound calls, the target_user_name may just be the dialed number
          // Try to resolve it to a known user name via extension list
          if (log.is_sip_call && log.direction === 'outbound' && log.target_number) {
            const cleanNumber = log.target_number.replace(/^\+/, '');
            const matchedExt = exts.find(
              ext => ext.extension_number === cleanNumber || ext.friendly_extension === cleanNumber
            );
            if (matchedExt?.user_id) {
              const matchedUser = users.find(u => u.id === Number(matchedExt.user_id));
              if (matchedUser) {
                return {
                  ...log,
                  target_user_name: `${matchedUser.first_name || ''} ${matchedUser.last_name || ''}`.trim() || log.target_user_name,
                  target_user_id: matchedUser.id,
                };
              }
            }
          }
          return log;
        });
        setCallLogs(enrichedLogs);
        // The API returns missedCount which spans all accessible clients when
        // all_clients=true. Use that for the badge so missed calls from any
        // client are visible regardless of the active client.
        const apiMissedCount = typeof data.missedCount === 'number' ? data.missedCount : 0;
        setMissedCallsCount(apiMissedCount);
        onMissedCallsChange?.(apiMissedCount);
      }
    } catch (e) {
      console.error('[TotalConnectRTC] Failed to fetch call logs:', e);
    } finally {
      setLoadingCallLogs(false);
    }
  }, [user, onMissedCallsChange]);

  // Mark missed calls as viewed (called when user opens recents tab)
  const markMissedCallsAsViewed = useCallback(async () => {
    // Use ref so we get the up-to-date count even inside stale closures
    if (missedCallsCountRef.current === 0) return;
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      const res = await fetch(buildApiUrl('/api/v1/operations/call-logs/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'mark_viewed', all_clients: true })
      });
      const data = await res.json();
      if (data.success) {
        setMissedCallsCount(0);
        // Update BadgeContext immediately so the Connect footer badge clears
        if (onMissedCallsChangeRef.current) onMissedCallsChangeRef.current(0);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error marking calls as viewed:', err);
    }
  }, []);
  
  const fetchCallSettings = useCallback(async () => {
    if (!user) return;
    try {
      const tokens = authService.getTokens();
      // Pass the selected PABX client_id so settings (SIP extension, DND, trunks)
      // are loaded for the client the user has selected in the multi-client dropdown.
      const settingsParams = new URLSearchParams();
      if (activeCallClientId) {
        settingsParams.set('client_id', String(activeCallClientId));
      }
      const settingsUrl = settingsParams.toString()
        ? buildApiUrl(`/api/v1/call-settings?${settingsParams.toString()}`)
        : buildApiUrl('/api/v1/call-settings');
      const res = await fetch(settingsUrl, {
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        setCallSettings(data.data);
        // Use the effective DND across all accessible clients when available
        // so incoming calls to any of the user's extensions are blocked if DND
        // is enabled on any client. Falls back to the per-client DND flag.
        const effectiveDnd =
          typeof data.data?.dnd_effective_across_clients === 'boolean'
            ? data.data.dnd_effective_across_clients
            : data.data?.do_not_disturb || false;
        setIsDNDEnabled(effectiveDnd);
      }
    } catch (e) {
      console.error('[TotalConnectRTC] Failed to fetch call settings:', e);
    }
    // Also load parking slot range for keypad interception
    try {
      const tokens = authService.getTokens();
      const res = await fetch(buildApiUrl('/api/v1/sip'), {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getParkingConfig',
          client_id: sipCallData?.client_id ?? activeCallClientId,
        })
      });
      const data = await res.json();
      if (data.success && data.data) {
        setParkingSlotRange({
          start: data.data.parking_lot_start || 701,
          end: data.data.parking_lot_end || 720
        });
      }
    } catch (e) {
      // Non-critical — keep default 701-720
    }
  }, [activeCallClientId, sipCallData?.client_id, user]);
  
  // ============================================
  // Screen Pop - CRM Contact Lookup
  // ============================================
  const fetchScreenPopData = useCallback(async (phoneNumber: string) => {
    setScreenPopLoading(true);
    setScreenPopData(null);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const screenPopParams = new URLSearchParams({
        phone: phoneNumber,
      });

      if (activeCallClientId) {
        screenPopParams.set('client_id', String(activeCallClientId));
      }

      const url = buildApiUrl(`/api/v1/sip/screen-pop?${screenPopParams.toString()}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setScreenPopData(data.data);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error fetching screen pop:', err);
    } finally {
      setScreenPopLoading(false);
    }
  }, [activeCallClientId]);
  
  // ============================================
  // Extensions Loading
  // ============================================
  const loadExtensions = useCallback(async () => {
    if (!activeCallClientId) return;
    setLoadingExtensions(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const url = buildApiUrl(`/api/v1/sip/extensions?client_id=${activeCallClientId}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });
      
      const data = await response.json();
      if (data.success && data.extensions) {
        const otherExtensions = (data.extensions as SipExtension[]).filter(
          (ext) => ext.user_id !== user?.id,
        );
        setExtensionList(otherExtensions);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error loading extensions:', err);
      toast.error('Failed to load extensions');
    } finally {
      setLoadingExtensions(false);
    }
  }, [activeCallClientId, user?.id]);
  
  // ============================================
  // Rolling Extension Resolution
  // ============================================
  // Resolves a friendly extension to its current dynamic extension.
  // If rolling is not enabled for the extension, returns the original.
  // This is a future-proof helper: currently all SIP operations go through
  // server APIs that resolve server-side, but if any future feature needs
  // to dial an extension directly (e.g. WebRTC-to-SIP bridge), this is ready.
  const resolveExtensionForDial = useCallback(async (friendlyExtension: string): Promise<{
    dialExtension: string;
    credentials?: { username: string; password: string };
  }> => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return { dialExtension: friendlyExtension };
      
      const response = await fetch(buildApiUrl('/api/v1/sip'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'resolveDynamicExtension',
          friendly_extension: friendlyExtension
        })
      });
      
      const data = await response.json();
      if (data.success && data.data?.dynamic_extension) {
        return {
          dialExtension: data.data.dynamic_extension,
          credentials: data.data.sip_credentials
        };
      }
      
      // Not rolling-enabled or not found — use the original
      return { dialExtension: friendlyExtension };
    } catch (err) {
      console.warn('[TotalConnectRTC] Extension resolution failed, using original:', err);
      return { dialExtension: friendlyExtension };
    }
  }, []);

  const publishLocalDtmfFallback = useCallback((code: number, digit: string) => {
    const localParticipant = roomRef.current?.localParticipant as
      | LocalParticipantDtmfCapable
      | undefined;

    if (typeof localParticipant?.publishDtmf === 'function') {
      localParticipant.publishDtmf(code, digit);
      return true;
    }

    return false;
  }, []);
  
  // ============================================
  // DTMF Sending (SIP calls)
  // ============================================
  const sendDtmf = useCallback((digit: string) => {
    // RFC 4733 event codes: 0-9 → 0-9, * → 10, # → 11
    const codeMap: Record<string, number> = {
      '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5,
      '6': 6, '7': 7, '8': 8, '9': 9, '*': 10, '#': 11,
    };
    const code = codeMap[digit];
    if (code === undefined) {
      console.warn('[TotalConnectRTC] Invalid DTMF digit:', digit);
      return;
    }

    // Send DTMF via LiveKit publishDtmf — the SDK emits a SipDTMF data packet
    // which livekit-sip converts to RFC2833 on the SIP leg (via Kamailio).
    try {
      if (!publishLocalDtmfFallback(code, digit)) {
        console.warn('[TotalConnectRTC] Cannot send DTMF - publishDtmf unavailable on local participant');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Failed to send DTMF:', err);
    }
  }, [publishLocalDtmfFallback]);
  
  // ============================================
  // Call Transfer Functions
  // ============================================
  
  /**
   * Mute or unmute the SIP participant's audio tracks locally.
   * During consultation (attended transfer), User A should not hear the
   * SIP participant (TTS / external caller) — only the transfer target.
   * This does NOT affect what the SIP side hears; it only controls
   * the local audio output in the browser.
   */
  const setSipParticipantLocalMute = useCallback((mute: boolean) => {
    const room = roomRef.current;
    if (!room) return;
    
    for (const [, participant] of room.remoteParticipants) {
      const id = participant.identity || '';
      const isSip = id.startsWith('sip-') || id.startsWith('sip_') ||
                    id.startsWith('+') || /^\d{10,15}$/.test(id);
      if (!isSip) continue;
      
      participant.audioTrackPublications.forEach(pub => {
        if (pub.track) {
          const mediaTrack = pub.track.mediaStreamTrack;
          if (mediaTrack) {
            mediaTrack.enabled = !mute;
          }
        }
      });
    }
  }, []);
  
  const blindTransfer = useCallback(async (targetExtension: string, targetName: string) => {
    if (!callIdRef.current) return;
    
    callNotifications.hapticFeedback('medium');
    setTransferringToExtension(targetExtension);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      // Put call on hold first
      if (!callControls.isOnHold) {
        await callControls.toggleHold();
      }
      
      const response = await fetch(buildApiUrl('/api/v1/sip/transfer'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          call_id: callIdRef.current,
          call_log_id: sipCallData?.call_log_id,
          client_id: sipCallData?.client_id,
          transfer_type: 'blind',
          target_extension: targetExtension,
          target_name: targetName
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Transferring to ${targetName}...`);
        setShowTransferDialog(false);
        
        // Stay connected on hold, wait for transfer result
        setPendingBlindTransfer({
          active: true,
          targetExtension,
          targetName,
          startedAt: Date.now()
        });
        pendingBlindTransferRef.current = true;
        pendingTransferTypeRef.current = 'blind';
        
        toast.loading(`Waiting for ${targetName} to answer...`, {
          id: 'blind-transfer-pending',
          duration: 60000
        });
      } else {
        toast.error(data.error || 'Transfer failed');
        if (callControls.isOnHold) await callControls.toggleHold();
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Blind transfer error:', err);
      toast.error('Transfer failed');
      if (callControls.isOnHold) await callControls.toggleHold();
    } finally {
      setTransferringToExtension(null);
    }
  }, [sipCallData, callControls, callNotifications]);
  
  // Server-side hold/MOH for a specific SIP leg (dual-line swap uses both rooms).
  const sipHoldMusicLeg = useCallback(
    async (
      hold: boolean,
      leg: {
        call_log_id?: string;
        livekit_room?: string;
        client_id?: number;
        callId: string | null;
      } | null,
    ) => {
      const tokens = authService.getTokens();
      if (!tokens?.access_token || !leg?.call_log_id) return { success: false };
      const roomName = leg.livekit_room || leg.callId;
      if (!roomName) return { success: false };
      try {
        const res = await fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'holdCall',
            call_id: roomName,
            call_log_id: leg.call_log_id,
            client_id: leg?.client_id ?? activeCallClientId,
            hold,
          }),
        });
        const data = await res.json();
        return data;
      } catch (err) {
        console.error(`[TotalConnectRTC] sipHoldMusicLeg(${hold}) error:`, err);
        return { success: false };
      }
    },
    [activeCallClientId],
  );

  // Helper: call hold-music API directly without disabling User A's mic.
  // Unlike toggleHold(), this only controls the SIP participant's server-side
  // mute and hold music. User A's mic stays live for consultation.
  const sipHoldMusic = useCallback(
    async (hold: boolean) => {
      if (!callIdRef.current) return { success: false };
      return sipHoldMusicLeg(hold, {
        call_log_id: sipCallData?.call_log_id,
        livekit_room: sipCallData?.livekit_room,
        client_id: sipCallData?.client_id,
        callId: callIdRef.current,
      });
    },
    [sipCallData, sipHoldMusicLeg],
  );

  sipHoldMusicRef.current = sipHoldMusic;

  const declineSecondLineCall = useCallback(() => {
    const pending = secondLineIncoming;
    if (!pending?.call_id) {
      setSecondLineIncoming(null);
      callNotifications.stopRingtone();
      return;
    }
    if (wsClient.isConnected()) {
      wsClient.send({
        type: 'call_declined',
        call_id: pending.call_id,
        reason: 'declined',
        target_user_id: pending.caller_id,
      });
    }
    setSecondLineIncoming(null);
    callNotifications.stopRingtone();
  }, [secondLineIncoming, callNotifications]);

  const answerSecondLineCall = useCallback(async () => {
    const pending = secondLineIncoming;
    if (
      !pending?.call_id ||
      !pending.sipData?.livekit_token ||
      !pending.sipData?.livekit_host
    ) {
      toast.error('Cannot answer second line');
      return;
    }
    callNotifications.stopRingtone();
    try {
      await sipHoldMusic(true);
      await roomRef.current?.localParticipant.setMicrophoneEnabled(false);
    } catch {
      /* ignore */
    }

    const cid = pending.call_id;
    if (wsClient.isConnected()) {
      wsClient.send({
        type: 'answer_sip_call',
        call_id: cid,
        call_log_id: pending.sipData.call_log_id,
      });
    }

    const roomName = pending.sipData.livekit_room || cid;
    const connected = await callSessionSecondary.connectToRoom(
      roomName,
      'voice',
      pending.sipData.livekit_token,
      pending.sipData.livekit_host,
    );

    if (!connected) {
      toast.error('Failed to connect second line');
      try {
        await sipHoldMusic(false);
        await roomRef.current?.localParticipant.setMicrophoneEnabled(true);
        callControls.syncLocalParticipantUiFromRoom?.(roomRef.current, false);
      } catch {
        /* ignore */
      }
      return;
    }

    callIdRefSecondary.current = cid;
    setSipCallDataSecondary({
      ...pending.sipData,
      caller_number: pending.caller_number,
    } as SipCallData);
    setSelectedUserSecondary({
      id: pending.caller_id,
      email: '',
      first_name: pending.caller_name?.split(' ')[0] || 'Incoming',
      last_name:
        pending.caller_number ||
        pending.caller_name?.split(' ').slice(1).join(' ') ||
        '',
      role: 'External',
      module_role: '',
    });
    secondaryElapsedStartRef.current = Date.now();
    setFocusedLine('secondary');
    setSecondLineIncoming(null);
    secondaryLineOccupiedRef.current = true;
    try {
      await roomRefSecondary.current?.localParticipant.setMicrophoneEnabled(true);
    } catch {
      /* ignore */
    }
    toast.success('Second line connected');
    callControls.syncLocalParticipantUiFromRoom?.(
      roomRefSecondary.current,
      false,
    );
  }, [
    secondLineIncoming,
    callNotifications,
    sipHoldMusic,
    callSessionSecondary,
    callControls,
  ]);

  const selectActiveCallLine = useCallback(
    async (lineId: string) => {
      if (lineId !== 'primary' && lineId !== 'secondary') return;

      const secondaryLive =
        !!sipCallDataSecondary &&
        roomRefSecondary.current?.state === ConnectionState.Connected;
      const primaryLive =
        !!sipCallData &&
        roomRef.current?.state === ConnectionState.Connected;

      if (lineId === 'secondary') {
        if (!secondaryLive) return;
        if (primaryLive) {
          await sipHoldMusicLeg(true, {
            call_log_id: sipCallData?.call_log_id,
            livekit_room: sipCallData?.livekit_room,
            client_id: sipCallData?.client_id,
            callId: callIdRef.current,
          });
        }
        await sipHoldMusicLeg(false, {
          call_log_id: sipCallDataSecondary?.call_log_id,
          livekit_room: sipCallDataSecondary?.livekit_room,
          client_id: sipCallDataSecondary?.client_id,
          callId: callIdRefSecondary.current,
        });
        await roomRef.current?.localParticipant.setMicrophoneEnabled(false);
        await roomRefSecondary.current?.localParticipant.setMicrophoneEnabled(true);
        setFocusedLine('secondary');
        callControls.syncLocalParticipantUiFromRoom?.(
          roomRefSecondary.current,
          false,
        );
        return;
      }

      if (lineId === 'primary') {
        if (!primaryLive) return;
        if (secondaryLive) {
          await sipHoldMusicLeg(true, {
            call_log_id: sipCallDataSecondary?.call_log_id,
            livekit_room: sipCallDataSecondary?.livekit_room,
            client_id: sipCallDataSecondary?.client_id,
            callId: callIdRefSecondary.current,
          });
        }
        await sipHoldMusicLeg(false, {
          call_log_id: sipCallData?.call_log_id,
          livekit_room: sipCallData?.livekit_room,
          client_id: sipCallData?.client_id,
          callId: callIdRef.current,
        });
        await roomRefSecondary.current?.localParticipant.setMicrophoneEnabled(false);
        await roomRef.current?.localParticipant.setMicrophoneEnabled(true);
        setFocusedLine('primary');
        callControls.syncLocalParticipantUiFromRoom?.(roomRef.current, false);
      }
    },
    [sipCallData, sipCallDataSecondary, sipHoldMusicLeg, callControls],
  );

  const startAttendedTransfer = useCallback(async (targetExtension: string, targetName: string) => {
    if (!callIdRef.current) return;

    callNotifications.hapticFeedback('medium');
    setTransferringToExtension(targetExtension);

    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      await sipHoldMusic(true);

      setSipParticipantLocalMute(true);

      const response = await fetch(buildApiUrl('/api/v1/sip/transfer'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          call_id: callIdRef.current,
          call_log_id: sipCallData?.call_log_id,
          client_id: sipCallData?.client_id,
          transfer_type: 'attended',
          target_extension: targetExtension,
          target_name: targetName,
          action: 'initiate',
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Ringing ${targetName}...`);
        setAttendedTransferState({
          status: 'ringing',
          targetExtension,
          targetName,
          originalCallOnHold: true,
        });
        setPendingBlindTransfer({
          active: true,
          targetExtension,
          targetName,
          startedAt: Date.now(),
        });
        pendingBlindTransferRef.current = true;
        pendingTransferTypeRef.current = 'attended';
        setShowTransferDialog(false);
      } else {
        toast.error(data.error || 'Could not reach extension');
        setSipParticipantLocalMute(false);
        await sipHoldMusic(false);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Attended transfer error:', err);
      toast.error('Could not reach extension');
      setSipParticipantLocalMute(false);
      await sipHoldMusic(false);
    } finally {
      setTransferringToExtension(null);
    }
  }, [sipCallData, sipHoldMusic, callNotifications, setSipParticipantLocalMute]);

  const completeAttendedTransfer = useCallback(async () => {
    if (!callIdRef.current || (attendedTransferState.status !== 'consulting' && attendedTransferState.status !== 'connected')) return;

    setAttendedTransferSubmitting('complete');
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        toast.error(
          'Not signed in — refresh the page and try Complete transfer again.',
        );
        return;
      }

      const response = await fetch(buildApiUrl('/api/v1/sip/transfer'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          call_id: callIdRef.current,
          call_log_id: sipCallData?.call_log_id,
          client_id: sipCallData?.client_id,
          transfer_type: 'attended',
          target_extension: attendedTransferState.targetExtension,
          action: 'complete'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(
          attendedTransferState.targetName
            ? `Caller handed off to ${attendedTransferState.targetName}. You have left the call.`
            : 'Transfer complete — you have left the call.',
          { duration: 5500 },
        );
        // Unmute SIP participant server-side so User B can hear the caller/TTS.
        // We call the hold-music API directly (not toggleHold) to avoid
        // stale closure issues and unnecessary mic toggling.
        await sipHoldMusic(false);
        // Gracefully leave the room WITHOUT killing the SIP leg
        // The transfer target remains connected with the external caller
        leaveRoomAfterTransferRef.current?.();
      } else {
        toast.error(
          data.error ||
            'Could not complete transfer. Stay on consultation and try again, or cancel.',
        );
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Complete attended transfer error:', err);
      toast.error(
        'Could not complete transfer. Stay on consultation and try again, or cancel.',
      );
    } finally {
      setAttendedTransferSubmitting(null);
    }
  }, [sipCallData, attendedTransferState, sipHoldMusic]);

  /** Same LiveKit room as consultation — restore caller audio and stay bridged with both parties. */
  const mergeConsultationToConference = useCallback(async () => {
    if (!callIdRef.current || attendedTransferState.status !== 'consulting') return;

    setAttendedTransferSubmitting('merge');
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        toast.error(
          'Not signed in — refresh the page and try Merge conference again.',
        );
        return;
      }

      const response = await fetch(buildApiUrl('/api/v1/sip/transfer'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          call_id: callIdRef.current,
          call_log_id: sipCallData?.call_log_id,
          client_id: sipCallData?.client_id,
          transfer_type: 'attended',
          action: 'merge_conference',
        }),
      });

      const data = await response.json();
      if (data.success) {
        const serverHint =
          typeof data.message === 'string' && data.message.trim()
            ? data.message.trim()
            : '';
        const headline = attendedTransferState.targetName
          ? `Everyone on one bridge with ${attendedTransferState.targetName}.`
          : 'Everyone on one bridge.';
        toast.success(
          serverHint ? `${headline} ${serverHint}` : headline,
          { duration: 6500 },
        );
        await sipHoldMusic(false);
        setSipParticipantLocalMute(false);
        setAttendedTransferState({ status: 'idle' });
        setPendingBlindTransfer({ active: false });
        pendingBlindTransferRef.current = false;
        setIsGroupCall(true);
      } else {
        toast.error(
          data.error ||
            'Could not merge everyone onto one bridge. Stay on consultation and try again, or cancel.',
        );
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Merge conference error:', err);
      toast.error(
        'Could not merge everyone onto one bridge. Stay on consultation and try again, or cancel.',
      );
    } finally {
      setAttendedTransferSubmitting(null);
    }
  }, [
    sipCallData?.call_log_id,
    sipCallData?.client_id,
    attendedTransferState.status,
    attendedTransferState.targetName,
    sipHoldMusic,
    setSipParticipantLocalMute,
  ]);
  
  const cancelAttendedTransfer = useCallback(async () => {
    if (!callIdRef.current) return;
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const response = await fetch(buildApiUrl('/api/v1/sip/transfer'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          call_id: callIdRef.current,
          call_log_id: sipCallData?.call_log_id,
          client_id: sipCallData?.client_id,
          transfer_type: 'attended',
          action: 'cancel'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Returned to original call');
        setAttendedTransferState({ status: 'idle' });
        pendingTransferTypeRef.current = null;
        // Unmute SIP participant locally and server-side
        setSipParticipantLocalMute(false);
        await sipHoldMusic(false);
      } else {
        toast.error(data.error || 'Could not return to call');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Cancel attended transfer error:', err);
      toast.error('Could not return to call');
    }
  }, [sipCallData, sipHoldMusic, setSipParticipantLocalMute]);
  
  // Cancel a pending blind transfer and return to the original call
  const cancelBlindTransfer = useCallback(async () => {
    if (!callIdRef.current) return;
    
    toast.dismiss('blind-transfer-pending');
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      // Notify the WebSocket server to cancel the transfer timer and reject the target
      const response = await fetch(buildApiUrl('/api/v1/sip/transfer'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          call_id: callIdRef.current,
          call_log_id: sipCallData?.call_log_id,
          client_id: sipCallData?.client_id,
          transfer_type: 'blind',
          action: 'cancel'
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Transfer canceled - resuming call');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Cancel blind transfer error:', err);
    }
    
    // Always restore the call locally regardless of API result
    setPendingBlindTransfer({ active: false });
    pendingBlindTransferRef.current = false;
    pendingTransferTypeRef.current = null;
    setAttendedTransferState({ status: 'idle' });
    setTransferringToExtension(null);
    
    // Unmute SIP participant locally and server-side
    setSipParticipantLocalMute(false);
    await sipHoldMusic(false);
  }, [sipCallData, sipHoldMusic, setSipParticipantLocalMute]);
  
  // ============================================
  // Graceful Room Leave (for completed transfers)
  // ============================================
  // Leaves the LiveKit room WITHOUT terminating the SIP call or sending
  // end_operations_call. The SIP participant and transfer target remain
  // connected in the room. This is the key difference from endCall().
  const leaveRoomAfterTransfer = useCallback(() => {
    
    // Stop ringtones/notifications
    callNotifications.stopRingtone();
    callNotifications.stopDialTone();
    callNotifications.cancelCallNotification();
    
    // Mute our audio first so caller doesn't hear us during disconnect
    try {
      callSession.room?.localParticipant?.setMicrophoneEnabled(false);
    } catch (e) { /* non-critical */ }
    
    // Disconnect from the LiveKit room — but do NOT call terminateCall
    callSession.disconnectFromRoom('transfer_completed');
    
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    
    // Disable Tauri desktop call mode
    if (isTauri()) {
      disableCallMode();
    }
    
    // NOTE: We intentionally skip:
    // - SIP terminateCall (SIP leg must stay alive for the transfer target)
    // - end_operations_call websocket message
    // - REST /api/v1/calls action: 'end'
    // The call continues between the transfer target and the SIP caller.
    
    // Reset all local state
    setCallState('idle');
    callStateRef.current = 'idle';
    setViewMode('contacts');
    setCallDuration(0);
    setSelectedUser(null);
    setSipCallData(null);
    setScreenPopData(null);
    setIsGroupCall(false);
    setGroupCallInfo(null);
    setActivityLog([]);
    setActiveReactions([]);
    setParticipantHandRaises(new Map());
    setWaitingParticipants([]);
    setShowWhiteboard(false);
    setWhiteboardElements([]);
    setWhiteboardUIMode({ kind: 'idle' });
    setRemotePresenterInfo(null);
    setIsRecording(false);
    setRecordingStartTime(null);
    setIsBreakoutActive(false);
    setBreakoutRooms([]);
    setCurrentBreakoutRoom(null);
    setShowBreakoutModal(false);
    setAddingParticipant(null);
    setShowInviteLink(false);
    setInviteLink(null);
    setGeneratingLink(false);
    setAttendedTransferState({ status: 'idle' });
    setPendingBlindTransfer({ active: false });
    pendingBlindTransferRef.current = false;
    pendingTransferTypeRef.current = null;
    isTransferTargetRef.current = false;
    setTransferringToExtension(null);
    setShowTransferDialog(false);
    setShowParkingDialog(false);
    isSipCallRef.current = false;
    sipCallLogIdRef.current = null;
    callIdRef.current = null;
    callStartTimeRef.current = null;
    pendingConnectionRef.current = null;
    onClearIncomingCall?.();
  }, [callSession, callNotifications, onClearIncomingCall, disableCallMode]);

  useEffect(() => {
    leaveRoomAfterTransferRef.current = leaveRoomAfterTransfer;
  }, [leaveRoomAfterTransfer]);
  
  // ============================================
  // Call Parking Functions
  // ============================================
  const loadParkingSlots = useCallback(async () => {
    if (!activeCallClientId) return;
    setLoadingParkingSlots(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const response = await fetch(buildApiUrl('/api/v1/sip'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getParkingSlots',
          client_id: sipCallData?.client_id ?? activeCallClientId,
        })
      });
      
      const data = await response.json();
      if (data.success && data.data) {
        setParkingSlots(data.data.slots || []);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error loading parking slots:', err);
      toast.error('Failed to load parking slots');
    } finally {
      setLoadingParkingSlots(false);
    }
  }, [activeCallClientId, sipCallData?.client_id]);
  
  const parkCall = useCallback(async (slot: number) => {
    if (!callIdRef.current || !sipCallData?.call_log_id) {
      toast.error('No active SIP call to park');
      return;
    }
    
    callNotifications.hapticFeedback('medium');
    setParkingToSlot(slot);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      // Apply hold music server-side (mutes SIP participant) WITHOUT
      // disabling our microphone — same pattern as attended transfer.
      await sipHoldMusic(true);
      
      const response = await fetch(buildApiUrl('/api/v1/sip'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'parkCall',
          call_id: callIdRef.current,
          call_log_id: sipCallData.call_log_id,
          client_id: sipCallData?.client_id ?? activeCallClientId,
          parking_slot: slot,
          livekit_room: sipCallData?.livekit_room || callIdRef.current
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Call parked on slot ${slot}`);
        setShowParkingDialog(false);
        // Leave the room without terminating the SIP leg — the SIP
        // participant stays in the LiveKit room with hold music playing.
        leaveRoomAfterTransfer();
      } else {
        toast.error(data.error || 'Failed to park call');
        // Undo hold on failure
        await sipHoldMusic(false);
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Park call error:', err);
      toast.error('Failed to park call');
      await sipHoldMusic(false);
    } finally {
      setParkingToSlot(null);
    }
  }, [activeCallClientId, sipCallData, sipHoldMusic, leaveRoomAfterTransfer, callNotifications]);
  
  // ============================================
  // Pickup Group 
  // ============================================
  const pickupGroupCall = useCallback(async (callId: string) => {
    callNotifications.hapticFeedback('medium');
    setPickingUpCall(callId);
    
    try {
      if (!wsClient.isConnected()) {
        toast.error('Unable to pick up call - realtime connection is offline');
        return;
      }

      wsClient.send({
        type: 'pickup_group_call',
        call_id: callId
      });
    } catch (err) {
      console.error('[TotalConnectRTC] Pickup group call error:', err);
      toast.error('Failed to pick up call');
    }
  }, [callNotifications]);
  
  // ============================================
  // DND Toggle
  // ============================================
  // When the user has PABX/Connect access across multiple clients, DND is
  // enforced across ALL of them so incoming calls to any extension are
  // blocked consistently. We use the `setDNDAllClients` action in that case;
  // otherwise we fall back to the single-client `setDND` action.
  const toggleDND = useCallback(async () => {
    callNotifications.hapticFeedback('medium');
    setDndLoading(true);

    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const newDndState = !isDNDEnabled;
      const useAllClients = hasMultiplePabxClients;

      const response = await fetch(buildApiUrl('/api/v1/call-settings'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: useAllClients ? 'setDNDAllClients' : 'setDND',
          enabled: newDndState
        })
      });

      const data = await response.json();
      if (data.success) {
        setIsDNDEnabled(newDndState);
        toast.success(
          newDndState
            ? (useAllClients ? 'Do Not Disturb enabled across all clients' : 'Do Not Disturb enabled')
            : 'Do Not Disturb disabled',
        );

        wsClient.send({
            type: 'dnd_status_changed',
            enabled: newDndState,
            user_id: user?.id
          });
      } else {
        toast.error(data.error || 'Failed to update DND');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error toggling DND:', err);
      toast.error('Failed to update Do Not Disturb');
    } finally {
      setDndLoading(false);
    }
  }, [isDNDEnabled, user?.id, callNotifications, hasMultiplePabxClients]);

  useEffect(() => {
    sipInboundMobileOnlyRef.current = callSettings?.sip_inbound_mobile_only === true;
  }, [callSettings?.sip_inbound_mobile_only]);

  const toggleSipInboundMobileOnly = useCallback(async () => {
    callNotifications.hapticFeedback('medium');
    setSipInboundMobileOnlyLoading(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      const newVal = !(callSettings?.sip_inbound_mobile_only === true);
      const response = await fetch(buildApiUrl('/api/v1/call-settings'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'setSipInboundMobileOnly',
          enabled: newVal,
        }),
      });
      const data = await response.json();
      if (data.success) {
        sipInboundMobileOnlyRef.current = newVal;
        setCallSettings((prev) =>
          prev ? { ...prev, sip_inbound_mobile_only: newVal } : prev,
        );
        toast.success(
          newVal
            ? 'Inbound SIP will ring on this phone app only'
            : 'Inbound SIP will ring on all your signed-in devices',
        );
      } else {
        toast.error(data.error || 'Failed to update preference');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error toggling SIP mobile-only:', err);
      toast.error('Failed to update preference');
    } finally {
      setSipInboundMobileOnlyLoading(false);
    }
  }, [callSettings?.sip_inbound_mobile_only, callNotifications]);
  
  // ============================================
  // Pop-out Call to Separate Window
  // ============================================
  const popOutToWindow = useCallback(async () => {
    if (isCapacitor() || !callIdRef.current) {
      return;
    }
    
    try {
      const callId = callIdRef.current;
      
      // Get a fresh LiveKit token for the popup window
      let tokens = authService.getTokens();
      if (!tokens?.access_token) {
        try {
          const stored = localStorage.getItem('auth_tokens');
          if (stored) tokens = JSON.parse(stored);
        } catch (e) {
          console.error('[TotalConnectRTC] Failed to read tokens from storage:', e);
        }
      }
      if (!tokens?.access_token) {
        toast.error('Unable to pop out - session expired. Please refresh the page.');
        return;
      }
      
      const response = await fetch(buildApiUrl('/api/v1/livekit/token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          // Use the room we're actually connected to — the call id is the room
          // name for SIP calls and REST-initiated calls; ops_call_ only applies
          // to legacy WS-initiated calls.
          room_name: roomRef.current?.name || callId,
          call_type: callType
        })
      });
      
      const data = await response.json();
      if (!data.success) {
        toast.error('Failed to get call token for popup');
        return;
      }
      
      const callerName = user?.first_name 
        ? `${user.first_name} ${user.last_name || ''}`.trim() 
        : user?.email || 'You';
      const targetName = selectedUser 
        ? `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || selectedUser.email
        : groupCallInfo?.threadName || 'Call';
      
      // Open call in popup window (isMeetingRoom=true means connect directly since we're already in the call)
      openCallPopup({
        callId,
        token: data.token,
        host: data.livekit_host || getLiveKitUrl(),
        type: callType,
        callerName,
        targetName,
        isMeetingRoom: true,
      });
      
      
      // Disconnect this modal from the room — popup will take over
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
      
      localTracksRef.current.forEach(track => track.stop());
      localTracksRef.current = [];
      
      callNotifications.stopRingtone();
      callNotifications.stopDialTone();
      
      // Close modal without ending the call
      setCallState('idle');
      setViewMode('contacts');
      setSelectedUser(null);
      setCallDuration(0);
      setIsGroupCall(false);
      setGroupCallInfo(null);
      onClose();
      
      toast.success('Call moved to popup window');
    } catch (err: unknown) {
      console.error('[TotalConnectRTC] Failed to pop out:', err);
      toast.error(getErrorMessage(err));
    }
  }, [callType, user, selectedUser, groupCallInfo, callNotifications, onClose]);
  
  // ============================================
  // Remind Me Handler - Schedule reminder before rejecting
  // ============================================
  const handleRemindMe = useCallback((minutes: number) => {
    const callerName = selectedUser
      ? `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || selectedUser.email
      : screenPopData?.contact?.contact_person || 'Unknown Caller';
    
    // Schedule a notification reminder
    const remindAt = Date.now() + minutes * 60 * 1000;
    
    // Use setTimeout for in-app reminder
    setTimeout(() => {
      toast(`Reminder: Call back ${callerName}`, {
        icon: '📞',
        duration: 10000,
      });
      
      // Also show a notification if available
      notify.info(`Reminder: Call back ${callerName}`);
    }, minutes * 60 * 1000);
    
    const label = minutes >= 60 ? `${minutes / 60} hour` : `${minutes} min`;
    toast.success(`Reminder set for ${label}`, { icon: '⏰', duration: 2000 });
  }, [selectedUser, screenPopData]);
  
  // ============================================
  // Quick Message Handler - Send text reply before rejecting
  // ============================================
  const handleQuickMessage = useCallback(async (message: string) => {
    const targetUserId = selectedUser?.id;
    
    if (!targetUserId) {
      toast.error('Cannot identify caller');
      return;
    }

    try {
      const response = await fetch('/api/v1/messages/quick-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_user_id: targetUserId,
          message,
        }),
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Quick reply sent', { icon: '💬', duration: 2000 });
      } else {
        console.error('[QuickReply] Failed:', data.error);
        toast.error('Failed to send reply');
      }
    } catch (err) {
      console.error('[QuickReply] Error:', err);
      toast.error('Failed to send reply');
    }
  }, [selectedUser]);
  
  // ============================================
  // Recording Toggle with WS Notification
  // ============================================
  const toggleRecording = useCallback(async () => {
    if (!callIdRef.current) {
      toast.error('No active call to record');
      return;
    }

    const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    const roomName = sipCallData?.livekit_room || callIdRef.current;
    const isSipRecording = isSipCallRef.current || Boolean(sipCallData?.call_log_id);
    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      toast.error('Unable to record - session expired');
      return;
    }
    
    if (isRecording) {
      try {
        const response = await fetch(buildApiUrl('/api/v1/calls'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'stop_recording',
            meeting_id: callIdRef.current,
            room_name: roomName,
            recording_id: activeRecordingId || undefined
          })
        });
        const data = await response.json();
        if (!data.success) {
          toast.error(data.error || 'Failed to stop recording');
          return;
        }
      } catch (err) {
        console.error('[TotalConnectRTC] Stop recording failed:', err);
        toast.error('Failed to stop recording');
        return;
      }

      setIsRecording(false);
      setRecordingStartTime(null);
      setActiveRecordingId(null);
      toast.success('Recording stopped');
      
      if (callIdRef.current) {
        wsClient.send({
          type: 'call_recording_stopped',
          call_id: callIdRef.current,
          user_id: user?.id,
          user_name: userName
        });
      }
    } else {
      try {
        const response = await fetch(buildApiUrl('/api/v1/calls'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'start_recording',
            meeting_id: callIdRef.current,
            room_name: roomName,
            sip_call_log_id: sipCallData?.call_log_id,
            recording_context: isSipRecording ? 'sip' : 'call'
          })
        });
        const data = await response.json();
        if (!data.success) {
          toast.error(data.error || 'Failed to start recording');
          return;
        }
        setActiveRecordingId(data.recording?.recording_id || null);
      } catch (err) {
        console.error('[TotalConnectRTC] Start recording failed:', err);
        toast.error('Failed to start recording');
        return;
      }

      setIsRecording(true);
      setRecordingStartTime(new Date());
      toast.success('Recording started');
      
      if (callIdRef.current) {
        wsClient.send({
          type: 'call_recording_started',
          call_id: callIdRef.current,
          user_id: user?.id,
          user_name: userName
        });
      }
    }
    callNotifications.hapticFeedback('medium');
  }, [activeRecordingId, isRecording, sipCallData, user, callNotifications]);
  
  // ============================================
  // Whiteboard Drawing (send to other participants via WS)
  // ============================================
  const handleSendDrawing = useCallback((element: DrawingElement) => {
    if (!callIdRef.current || whiteboardUIModeRef.current.kind === 'following') return;
    wsClient.send({
      type: 'call_whiteboard_draw',
      call_id: callIdRef.current,
      user_id: user?.id,
      element,
    });
  }, [user?.id]);
  
  // ============================================
  // Generate Invite Link for current call
  // ============================================
  const generateInviteLink = useCallback(async () => {
    if (!callIdRef.current) {
      toast.error('No active call', { duration: 3000 });
      return;
    }
    
    setGeneratingLink(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const url = buildApiUrl('/api/v1/calls');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'generate_guest_link',
          call_id: callIdRef.current,
          // The LiveKit room we're actually in — guests must land in the same
          // room (call ids and room names differ for WS-initiated calls).
          room_name: roomRef.current?.name || undefined,
          expires_in_minutes: 60,
          max_uses: 5
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setInviteLink(data.join_url);
        setShowInviteLink(true);
      } else {
        toast.error(data.error || 'Failed to generate link', { duration: 3000 });
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Error generating invite link:', err);
      toast.error('Failed to generate invite link', { duration: 3000 });
    } finally {
      setGeneratingLink(false);
    }
  }, []);
  
  // ============================================
  // Copy Invite Link to clipboard
  // ============================================
  const copyInviteLink = useCallback(async () => {
    if (!inviteLink) return;
    
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success('Link copied to clipboard', { duration: 2000 });
    } catch (err) {
      console.error('[TotalConnectRTC] Error copying link:', err);
      // Fallback for mobile
      const textArea = document.createElement('textarea');
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied', { duration: 2000 });
    }
  }, [inviteLink]);
  
  // ============================================
  // Breakout Rooms - Start
  // ============================================
  const startBreakoutRooms = useCallback((rooms: Array<{
    id: string;
    name: string;
    participants: Array<{ id: number; name: string; avatar?: string }>;
  }>, duration?: number) => {
    if (!callIdRef.current) return;
    
    wsClient.send({
      type: 'start_breakout_rooms',
      call_id: callIdRef.current,
      rooms: rooms,
      duration: duration
    });
    
    setBreakoutRooms(rooms);
    setIsBreakoutActive(true);
    setShowBreakoutModal(false);
    
    // Find which room current user is in
    for (const room of rooms) {
      const found = room.participants.find(p => p.id === user?.id);
      if (found) {
        setCurrentBreakoutRoom(room.id);
        break;
      }
    }
    
    toast.success('Breakout rooms started');
  }, [user]);
  
  // ============================================
  // Breakout Rooms - End
  // ============================================
  const endBreakoutRooms = useCallback(() => {
    if (!callIdRef.current) return;
    
    wsClient.send({
      type: 'end_breakout_rooms',
      call_id: callIdRef.current
    });
    
    setBreakoutRooms([]);
    setIsBreakoutActive(false);
    setCurrentBreakoutRoom(null);
    setShowBreakoutModal(false);
    
    toast.success('Breakout rooms ended');
  }, []);
  
  // ============================================
  // Lobby - Admit/Deny participants from waiting room
  // ============================================
  const admitFromLobby = useCallback(async (participantId: number) => {
    // Find the guest — if participantId is 0, it's an external guest
    const guest = waitingParticipants.find(p => p.userId === participantId);
    const isExternalGuest = guest && guest.userId === 0 && guest.identity;

    if (!isExternalGuest) {
      // Internal user flow via WebSocket
      if (!callIdRef.current) return;
      wsClient.send({
        type: 'admit_from_lobby',
        call_id: callIdRef.current,
        user_id: participantId
      });
      setWaitingParticipants(prev => prev.filter(p => p.userId !== participantId));
      toast.success('Participant admitted');
      return;
    }

    // External guest flow via API
    try {
      const tokens = authService.getTokens();
      const response = await fetch(buildApiUrl('/api/v1/calls'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens?.access_token}`
        },
        body: JSON.stringify({
          action: 'admit_guest',
          meeting_id: callIdRef.current?.replace('meeting-', ''),
          guest_identity: guest.identity
        })
      });
      const data = await response.json();
      if (data.success) {
        setWaitingParticipants(prev => prev.filter(p => p.identity !== guest.identity));
        toast.success(`${guest.name} admitted`);
      } else {
        toast.error(data.error || 'Failed to admit guest');
      }
    } catch (error) {
      console.error('[TotalConnectRTC] Error admitting guest:', error);
      toast.error('Failed to admit guest');
    }
  }, [waitingParticipants]);

  const denyFromLobby = useCallback(async (participantId: number) => {
    const guest = waitingParticipants.find(p => p.userId === participantId);
    const isExternalGuest = guest && guest.userId === 0 && guest.identity;

    if (!isExternalGuest) {
      // Internal user flow via WebSocket
      if (!callIdRef.current) return;
      wsClient.send({
        type: 'deny_from_lobby',
        call_id: callIdRef.current,
        user_id: participantId
      });
      setWaitingParticipants(prev => prev.filter(p => p.userId !== participantId));
      return;
    }

    // External guest flow via API
    try {
      const tokens = authService.getTokens();
      const response = await fetch(buildApiUrl('/api/v1/calls'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens?.access_token}`
        },
        body: JSON.stringify({
          action: 'deny_guest',
          meeting_id: callIdRef.current?.replace('meeting-', ''),
          guest_identity: guest.identity
        })
      });
      const data = await response.json();
      if (data.success) {
        setWaitingParticipants(prev => prev.filter(p => p.identity !== guest.identity));
        toast(`${guest.name} denied entry`);
      } else {
        toast.error(data.error || 'Failed to deny guest');
      }
    } catch (error) {
      console.error('[TotalConnectRTC] Error denying guest:', error);
      toast.error('Failed to deny guest');
    }
  }, [waitingParticipants]);

  // ============================================
  // Transfer/Park Call to another Device
  // ============================================
  const transferCallToDevice = useCallback(async (sessionId: string) => {
    if (!callIdRef.current) return;
    setTransferringTo(sessionId);

    try {
      const tokens = authService.getTokens();

      // For SIP calls, put the call on hold first so the caller hears hold music
      // while the transfer target device picks up.
      if (isSipCall && sipCallData?.call_log_id && !callControls.isOnHold) {
        await sipHoldMusic(true);
      }

      const response = await fetch(buildApiUrl('/api/v1/calls'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'transfer_request',
          call_id: callIdRef.current,
          target_session_id: sessionId,
          client_id: sipCallData?.client_id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(
          data.ws_notified === false
            ? 'Transfer request sent — waiting for the other device to accept'
            : 'Transfer request sent to device'
        );
      } else {
        toast.error(data.error || 'Transfer failed');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Transfer to device error:', err);
      toast.error('Transfer failed');
    } finally {
      setTransferringTo(null);
    }
  }, [isSipCall, sipCallData, callControls.isOnHold, sipHoldMusic]);
  
  // ============================================
  // Add Participant to Call
  // ============================================
  const addParticipantToCall = useCallback(async (targetUser: OperationsUser) => {
    if (!callIdRef.current) {
      toast.error('No active call', { duration: 3000 });
      return;
    }
    
    setAddingParticipant(targetUser.id);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const url = buildApiUrl('/api/v1/calls');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'add_participants',
          call_id: callIdRef.current,
          user_ids: [targetUser.id]
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`Calling ${targetUser.first_name}...`, { duration: 3000 });
        setIsGroupCall(true);
      } else {
        toast.error(data.error || 'Failed to add participant', { duration: 3000 });
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Add participant error:', err);
      toast.error('Failed to add participant', { duration: 3000 });
    } finally {
      setAddingParticipant(null);
    }
  }, []);
  
  // ============================================
  // LiveKit Token Helper (for SIP calls that need token fetch)
  // ============================================
  const getLivekitToken = useCallback(async (roomName: string, type: CallType) => {
    try {
      const tokens = authService.getTokens();
      const response = await fetch(buildApiUrl('/api/v1/livekit/token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens?.access_token || ''}`
        },
        body: JSON.stringify({
          room_name: roomName,
          call_type: type
        })
      });

      const data = await response.json();
      if (data.success) {
        return { host: data.livekit_host, token: data.token };
      }
      console.error('[TotalConnectRTC] Token fetch failed:', data.error);
      return null;
    } catch (err) {
      console.error('[TotalConnectRTC] Token fetch error:', err);
      return null;
    }
  }, []);
  
  // ============================================
  // Retrieve Parked Call (must be after getLivekitToken)
  // ============================================
  const retrieveParkedCall = useCallback(async (slot: number) => {
    // Prevent retrieval if already in an active call
    const activeCallStates: CallState[] = ['connected', 'connecting', 'reconnecting'];
    if (activeCallStates.includes(callState)) {
      toast.error('Cannot retrieve — you are already on a call');
      return;
    }
    
    callNotifications.hapticFeedback('medium');
    setParkingToSlot(slot);
    
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const response = await fetch(buildApiUrl('/api/v1/sip'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'retrieveParkedCall',
          client_id: sipCallData?.client_id ?? activeCallClientId,
          parking_slot: slot
        })
      });
      
      const data = await response.json();
      if (data.success && data.data?.livekit_room) {
        const roomName = data.data.livekit_room;
        
        // Get a LiveKit token so we can join the room where the
        // SIP participant is still alive with hold music playing
        const tokenData = await getLivekitToken(roomName, 'voice');
        if (!tokenData) {
          toast.error('Failed to get call token');
          return;
        }
        
        // Connect to the LiveKit room
        const connected = await callSession.connectToRoom(
          roomName,
          'voice',
          tokenData.token,
          tokenData.host
        );
        
        if (!connected) {
          toast.error('Failed to connect to parked call');
          return;
        }
        
        // Set SIP call data so the UI enters in-call mode
        const callLogId = data.data.call_id;
        callIdRef.current = roomName;
        isSipCallRef.current = true;
        sipCallLogIdRef.current = callLogId;
        setSipCallData({
          call_log_id: callLogId,
          client_id: data.data.client_id,
          livekit_room: roomName,
          caller_name: data.data.caller_name,
          caller_number: data.data.caller_number
        });
        setCallState('connected');
        callStateRef.current = 'connected';
        callStartTimeRef.current = Date.now();
        setViewMode('incall');
        setSelectedUser({
          id: 0,
          email: '',
          first_name: data.data.caller_name || data.data.caller_number || `Slot ${slot}`,
          last_name: '',
          role: 'External',
          module_role: '',
        });
        setShowParkingDialog(false);
        
        // Release hold music — unmute the SIP participant so audio flows
        // (The API already calls hold-music/stop, but if it didn't take
        // effect we fire it again from the client side to be safe.)
        if (!data.data.hold_released) {
          await sipHoldMusic(false);
        }
        
        toast.success(`Retrieved call from slot ${slot}`);
      } else {
        toast.error(data.error || 'Failed to retrieve call');
      }
    } catch (err) {
      console.error('[TotalConnectRTC] Retrieve parked call error:', err);
      toast.error('Failed to retrieve call');
    } finally {
      setParkingToSlot(null);
    }
  }, [activeCallClientId, sipCallData?.client_id, callState, callNotifications, callSession, getLivekitToken, sipHoldMusic]);
  
  // ============================================
  // Call Actions
  // ============================================
  const startCall = useCallback(async (targetUser: OperationsUser, type: CallType) => {
    if (!user) return;
    
    // Allow starting a call if not actively connected/connecting
    // This handles stale states like 'ringing' (missed call), 'ended', etc.
    const activeCallStates: CallState[] = ['connected', 'connecting', 'reconnecting'];
    if (activeCallStates.includes(callState)) {
      notify.error('You are currently on a call');
      return;
    }
    
    // Clean up any stale state from non-active states (ringing, initiating, ended)
    if (callState !== 'idle') {
      callNotifications.stopRingtone();
      callNotifications.stopDialTone();
      resetOutgoingProgressState(false);
      callSession.disconnectFromRoom('stale_cleanup');
      if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
      callIdRef.current = null;
      pendingConnectionRef.current = null;
      onClearIncomingCall?.();
    }
    
    setSelectedUser(targetUser);
    setCallType(type);
    setCallState('initiating');
    callStateRef.current = 'initiating';
    setViewMode('incall');
    outgoingInternalCallRef.current = true;
    setOutgoingProgressState('connecting');

    // Notify BiometricLockScreen to bypass lock for outgoing call
    try {
      window.dispatchEvent(new CustomEvent('totalaccess-call-active', { detail: { direction: 'outgoing' } }));
    } catch {}
    
    // Caller-side dial timeout - stop dialing after 50s if no answer
    if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
    ringTimeoutRef.current = setTimeout(() => {
      if (callStateRef.current === 'initiating') {
        endCallRef.current?.('unanswered');
        notify.error('No answer');
      }
    }, 50000);
    
    try {
      const tokens = authService.getTokens();
      
      // Call REST API to initiate - it creates the DB record, generates LiveKit token,
      // and notifies the receiver via WS server's /api/calls/notify endpoint.
      // NOTE: We no longer send a direct WS 'initiate_operations_call' here because
      // the REST API already handles notification. Sending both caused duplicate
      // incoming calls with different call IDs (client-generated vs UUID).
      const res = await fetch(buildApiUrl('/api/v1/calls'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'initiate',
          target_user_ids: [targetUser.id],
          call_type: type,
        }),
      });
      
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to initiate call');
      }
      
      // Update call ID from server if provided
      if (data.call?.call_id) {
        callIdRef.current = data.call.call_id;
      }

      setOutgoingProgressState('calling');

      const delivery = data.call?.delivery || data.delivery;
      const recipientReached = !!(
        delivery?.ws_notified ||
        delivery?.fcm_sent ||
        delivery?.fcm_requested ||
        delivery?.ws_in_foreground
      );

      if (recipientReached) {
        clearQueuedOutgoingRingingTransition();
        queuedOutgoingRingingTimeoutRef.current = setTimeout(() => {
          if (
            outgoingInternalCallRef.current &&
            callStateRef.current === 'initiating' &&
            callIdRef.current === data.call?.call_id
          ) {
            setOutgoingProgressState('ringing');
          }
        }, 180);
      }
      
      // If server already provides room/token, store as pending connection
      // Don't connect yet - wait for receiver to answer (call_answered event)
      if (data.call?.livekit_token && data.call?.livekit_host) {
        pendingConnectionRef.current = {
          host: data.call.livekit_host,
          token: data.call.livekit_token,
          type: type
        };
      } else {
        // Fetch LiveKit token for when the receiver answers
        // Room name must match the meeting ID (UUID) used by the REST API
        const tokenData = await getLivekitToken(callIdRef.current!, type);
        if (tokenData) {
          pendingConnectionRef.current = {
            host: tokenData.host,
            token: tokenData.token,
            type: type
          };
        }
      }
      
    } catch (e: unknown) {
      console.error('[TotalConnectRTC] Failed to start call:', e);
      resetOutgoingProgressState();
      setCallState('idle');
      callStateRef.current = 'idle';
      const errorMessage = getErrorMessage(e);
      setError(errorMessage);
      notify.error(errorMessage || 'Failed to start call');
    }
  }, [user, callState, callSession, callNotifications, getLivekitToken, onClearIncomingCall, resetOutgoingProgressState, setOutgoingProgressState, clearQueuedOutgoingRingingTransition]);

  // ============================================
  // Group / thread call initiation (openGroupCallModal events)
  // ============================================
  const startGroupCall = useCallback(async (group: PendingGroupCall) => {
    if (!user) return;

    const participantIds = (group.participantIds || []).filter((id) => id !== user.id);
    const type: CallType = group.callType || 'voice';

    const activeCallStates: CallState[] = ['connected', 'connecting', 'reconnecting'];
    if (activeCallStates.includes(callState)) {
      notify.error('You are currently on a call');
      return;
    }

    // Clean up any stale state from non-active states
    if (callState !== 'idle') {
      callNotifications.stopRingtone();
      callNotifications.stopDialTone();
      resetOutgoingProgressState(false);
      callSession.disconnectFromRoom('stale_cleanup');
      if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
      callIdRef.current = null;
      pendingConnectionRef.current = null;
      onClearIncomingCall?.();
    }

    setIsGroupCall(true);
    setGroupCallInfo({
      threadId: group.threadId,
      threadName: group.threadName,
      participantCount: participantIds.length + 1,
      callType: type,
    });
    setSelectedUser({
      id: 0,
      email: '',
      first_name: group.threadName || 'Group Call',
      last_name: '',
      role: '',
      module_role: '',
    });
    setCallType(type);
    setCallState('initiating');
    callStateRef.current = 'initiating';
    setViewMode('incall');
    outgoingInternalCallRef.current = true;
    setOutgoingProgressState('connecting');

    try {
      window.dispatchEvent(new CustomEvent('totalaccess-call-active', { detail: { direction: 'outgoing' } }));
    } catch {}

    if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
    ringTimeoutRef.current = setTimeout(() => {
      if (callStateRef.current === 'initiating') {
        endCallRef.current?.('unanswered');
        notify.error('No answer');
      }
    }, 50000);

    try {
      const tokens = authService.getTokens();

      const res = await fetch(buildApiUrl('/api/v1/calls'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'initiate',
          target_user_ids: participantIds,
          call_type: type,
          thread_id: group.threadId,
          thread_name: group.threadName,
          is_group_call: true,
          ...(group.isMeeting ? { is_meeting_room: true, title: group.threadName } : {}),
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to start group call');
      }

      if (data.call?.call_id) {
        callIdRef.current = data.call.call_id;
      }

      setOutgoingProgressState('calling');

      const roomToken = data.call?.livekit_token;
      const roomHost = data.call?.livekit_host;
      if (roomToken && roomHost) {
        pendingConnectionRef.current = { host: roomHost, token: roomToken, type };
      } else {
        const tokenData = await getLivekitToken(callIdRef.current!, type);
        if (tokenData) {
          pendingConnectionRef.current = { host: tokenData.host, token: tokenData.token, type };
        }
      }

      // Meetings: the host enters the room immediately rather than waiting for
      // the first answer (participants may join much later, e.g. via link).
      if (group.isMeeting && pendingConnectionRef.current) {
        const { host, token } = pendingConnectionRef.current;
        pendingConnectionRef.current = null;
        const connected = await callSession.connectToRoom(callIdRef.current!, type, token, host);
        if (connected) {
          setCallState('connected');
          callStateRef.current = 'connected';
          callStartTimeRef.current = Date.now();
          if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }
        } else {
          throw new Error('Failed to connect to meeting room');
        }
      }
    } catch (e: unknown) {
      console.error('[TotalConnectRTC] Failed to start group call:', e);
      resetOutgoingProgressState();
      setIsGroupCall(false);
      setGroupCallInfo(null);
      setCallState('idle');
      callStateRef.current = 'idle';
      const errorMessage = getErrorMessage(e);
      setError(errorMessage);
      notify.error(errorMessage || 'Failed to start group call');
    }
  }, [user, callState, callSession, callNotifications, getLivekitToken, onClearIncomingCall, resetOutgoingProgressState, setOutgoingProgressState]);

  // Consume pending call targets passed via openCallModal / openGroupCallModal
  // events (contact call buttons, message-thread call buttons).
  useEffect(() => {
    if (!isOpen || !pendingCallTarget || !user) return;
    onPendingCallHandled?.();
    void startCall(pendingCallTarget.user, pendingCallTarget.callType);
  }, [isOpen, pendingCallTarget, user, startCall, onPendingCallHandled]);

  useEffect(() => {
    if (!isOpen || !pendingGroupCall || !user) return;
    onPendingGroupCallHandled?.();
    void startGroupCall(pendingGroupCall);
  }, [isOpen, pendingGroupCall, user, startGroupCall, onPendingGroupCallHandled]);

  const dialPadSuggestions = useMemo(
    () =>
      buildDialPadSuggestions(dialPadNumber, {
        operationsUsers,
        crmRows: showCrmContactsTab ? crmDialerRows : [],
        callLogs,
      }),
    [dialPadNumber, operationsUsers, crmDialerRows, callLogs, showCrmContactsTab],
  );

  const { dialPadSubmit, cancelOutboundDialInProgress } = useDialPadSubmit({
    callSettings,
    extensionList,
    operationsUsers,
    parkingSlotRange,
    activeCallClientId,
    startCall,
    retrieveParkedCall,
    connectToRoom: callSession.connectToRoom,
    callNotifications,
    callNotificationsRef,
    setDialPadNumber,
    setDialHistory,
    setIsDialingPSTN,
    setSelectedUser,
    setSipCallData,
    setCallState,
    setViewMode,
    callIdRef,
    sipCallLogIdRef,
    ringTimeoutRef,
    callStateRef,
    isSipCallRef,
    endCallRef,
  });

  const dispatchAnswerSignal = useCallback(
    async (
      payload: {
        type: 'answer_sip_call' | 'answer_operations_call';
        call_id: string;
        call_log_id?: string | null;
      },
      options?: {
        preferImmediateDispatch?: boolean;
        connectTimeoutMs?: number;
      },
    ): Promise<boolean> => {
      const preferImmediateDispatch = options?.preferImmediateDispatch === true;
      const connectTimeoutMs = options?.connectTimeoutMs ?? 1800;

      if (wsClient.isConnected()) {
        return wsClient.send(payload);
      }

      pendingAnswerMessageRef.current = payload;

      if (!preferImmediateDispatch) {
        return false;
      }

      try {
        const connected = await Promise.race<boolean>([
          wsClient.connect().then(() => wsClient.isConnected()).catch(() => false),
          new Promise<boolean>((resolve) => {
            setTimeout(() => resolve(false), connectTimeoutMs);
          }),
        ]);

        if (!connected || !wsClient.isConnected()) {
          return false;
        }

        const sent = wsClient.send(payload);
        if (sent) {
          pendingAnswerMessageRef.current = null;
        }

        return sent;
      } catch (e) {
        console.warn('[TotalConnectRTC] Failed to dispatch answer signal immediately:', e);
        return false;
      }
    },
    [],
  );

  // Test calls are handled in-room by the ws-go CallAgent (kind=test_call):
  // it plays the per-client pre-generated prompt, records the user's mic and
  // plays it back. The old browser-side TestCallHandler (speechSynthesis +
  // endCall on script completion) is intentionally not started — it raced the
  // agent and terminated the call when its script finished.

  const answerCall = useCallback(async () => {
    // Use ref for guard to avoid stale closure issues
    if (callStateRef.current !== 'ringing') return;
    
    // Resolve call ID from internal ref (set by WS handler) or prop (set by parent)
    const currentCallId = callIdRef.current || incomingCall?.call_id;
    if (!currentCallId) {
      console.error('[TotalConnectRTC] Cannot answer: no call_id available');
      return;
    }

    await primeBrowserCallAudio();

    if (isCapacitor()) {
      void (async () => {
        try {
          await LockScreen.enableShowOverLockScreen();
        } catch (e) {
          console.warn('[TotalConnectRTC] enableShowOverLockScreen during answer failed:', e);
        }

        try {
          const lockState = await LockScreen.isDeviceLocked();
          if (!lockState.isLocked) {
            return;
          }

          const dismissResult = await LockScreen.requestDismissKeyguard();
        } catch (e) {
          console.warn('[TotalConnectRTC] requestDismissKeyguard failed:', e);
        }
      })();
    }
    
    localAnsweringCallIdRef.current = currentCallId;
    markColdStartCallHandling(currentCallId);
    if (typeof window !== 'undefined') {
      (window as Window & { __stopCallConnectingFeedback?: () => void }).__stopCallConnectingFeedback?.();
    }
    callNotifications.stopRingtone();
    callNotifications.cancelCallNotification(); // Dismiss Android/FCM notification
    callNotifications.hapticFeedback('heavy');

    // IMPORTANT: Do not mark this call as handled yet.
    // During cold start the pending call can be re-read before LiveKit finishes
    // connecting. The cold-start effect treats handled markers as terminal and
    // would persist handled=true too early, making the answer flow unrecoverable.
    // Mark as handled in SAME-PAGE refs only — NOT in persistent storage.
    // confirmColdStartHandled is deferred until the call actually CONNECTS to the
    // LiveKit room. During cold start the WebView can reload 3+ times; if we
    // mark handled now (in localStorage) and the page navigates before the room
    // connects, the next page sees handled=true and skips the call entirely,
    // leaving the user on the dashboard. Refs prevent same-page re-processing.
    coldStartHandledRef.current = currentCallId;
    setMediaAudioBlocked(false);
    
    setCallDuration(0);
    setCallState('connecting');
    callStateRef.current = 'connecting';

    // Notify BiometricLockScreen to bypass lock for active call
    try {
      window.dispatchEvent(new CustomEvent('totalaccess-call-active', { detail: { callId: currentCallId, direction: 'incoming' } }));
    } catch {}
    
    // Clear ringing timeout since we're answering
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    
    const isSipInboundCall = isSipCallRef.current || incomingCall?.isSipCall || currentCallId.startsWith('sip-inbound-');
    
    // CRITICAL: If SIP detected via fallback (call_id prefix / prop) but sipCallData is null,
    // set it now from incomingCall.sipData so UI shows correct SIP buttons during the call.
    if (isSipInboundCall && !sipCallData && !isSipCallRef.current) {
      const propSipData = incomingCall?.sipData || {};
      setSipCallData({
        ...propSipData,
        livekit_room: propSipData.livekit_room || currentCallId,
      });
      isSipCallRef.current = true;
      sipCallLogIdRef.current = propSipData.call_log_id || null;
    }
    
    try {
      const answerPayload = {
        type: isSipInboundCall ? 'answer_sip_call' : 'answer_operations_call',
        call_id: currentCallId,
        call_log_id: sipCallData?.call_log_id || sipCallLogIdRef.current || incomingCall?.sipData?.call_log_id,
      } as const;

      const answerSignalSent = await dispatchAnswerSignal(answerPayload, {
        preferImmediateDispatch: isSipInboundCall,
      });

      if (isSipInboundCall && answerSignalSent) {
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 150);
        });
      }
      
      callIdRef.current = currentCallId;
      
      // For SIP calls with pre-generated token, use it directly (fastest path)
      // Also check sipCallDataRef for cold start scenarios where state update hasn't arrived yet
      const latestSipData = sipCallData || sipCallDataRef.current;
      if (isSipInboundCall && 
          (latestSipData?.livekit_token || incomingCall?.sipData?.livekit_token) && 
          (latestSipData?.livekit_host || incomingCall?.sipData?.livekit_host)) {
        const token = latestSipData?.livekit_token || incomingCall!.sipData!.livekit_token!;
        const host = latestSipData?.livekit_host || incomingCall!.sipData!.livekit_host!;

        const connected = await callSession.connectToRoom(
          latestSipData?.livekit_room || incomingCall?.sipData?.livekit_room || currentCallId,
          callType,
          token,
          host
        );
        if (!connected) throw new Error('Failed to connect to room');

        setCallState('connected');
        callStateRef.current = 'connected';
        callStartTimeRef.current = Date.now();
        lastHandledCallIdRef.current = currentCallId;
        // NOW mark as handled — call is actually connected to LiveKit room
        confirmColdStartHandled(currentCallId);
        if (typeof window !== 'undefined') {
          (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer = null;
          try {
            localStorage.removeItem('coldstart_pending_call');
            sessionStorage.removeItem('pending_call_answer');
            localStorage.removeItem('coldstart_call_handling');
          } catch (e) {}
          clearColdStartCallHandling();
        }
        return;
      }
      
      // For SIP calls without pre-gen token (e.g., cold start), wait briefly for WS data
      if (isSipInboundCall && !latestSipData?.livekit_token) {
        for (let attempt = 0; attempt < 6; attempt++) {
          await new Promise(r => setTimeout(r, 500));
          const freshSipData = sipCallDataRef.current;
          if (freshSipData?.livekit_token && freshSipData?.livekit_host) {
            const connected = await callSession.connectToRoom(
              freshSipData.livekit_room || currentCallId,
              callType,
              freshSipData.livekit_token,
              freshSipData.livekit_host
            );
            if (!connected) throw new Error('Failed to connect to room');
            setCallState('connected');
            callStateRef.current = 'connected';
            callStartTimeRef.current = Date.now();
            lastHandledCallIdRef.current = currentCallId;
            // NOW mark as handled — call is actually connected
            confirmColdStartHandled(currentCallId);
            if (typeof window !== 'undefined') {
              (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer = null;
              try {
                localStorage.removeItem('coldstart_pending_call');
                sessionStorage.removeItem('pending_call_answer');
                localStorage.removeItem('coldstart_call_handling');
              } catch (e) {}
              clearColdStartCallHandling();
            }
            return;
          }
        }
      }
      
      // For SIP calls without pre-gen token, or regular calls, use REST API
      if (isSipInboundCall) {
        // SIP call - get token via LiveKit API
        const roomName = latestSipData?.livekit_room || incomingCall?.sipData?.livekit_room || currentCallId;
        
        const tokenData = await getLivekitToken(roomName, callType);
        if (!tokenData) throw new Error('Failed to get LiveKit token');
        
        const connected = await callSession.connectToRoom(
          roomName,
          callType,
          tokenData.token,
          tokenData.host
        );
        if (!connected) throw new Error('Failed to connect to room');
      } else {
        // Regular call - use calls API
        const postAnswer = () => {
          const tokens = authService.getTokens();
          return fetch(buildApiUrl('/api/v1/calls'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${tokens?.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'answer',
              call_id: currentCallId,
            }),
          });
        };

        // The stored access token may be expired when the app was woken by a
        // push (cold start) or resumed from background — refresh once and retry.
        let res = await postAnswer();
        if (res.status === 401 && (await authService.refreshToken())) {
          res = await postAnswer();
        }

        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Failed to answer call');
        
        const connected = await callSession.connectToRoom(
          data.call.livekit_room, 
          callType,
          data.call.livekit_token,
          data.call.livekit_host
        );
        if (!connected) throw new Error('Failed to connect to room');
      }

      setCallState('connected');
      callStateRef.current = 'connected';
      callStartTimeRef.current = Date.now();
      lastHandledCallIdRef.current = currentCallId;
      // NOW mark as handled — call is actually connected to LiveKit room
      confirmColdStartHandled(currentCallId);
      if (typeof window !== 'undefined') {
        (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer = null;
        try {
          localStorage.removeItem('coldstart_pending_call');
          sessionStorage.removeItem('pending_call_answer');
          localStorage.removeItem('coldstart_call_handling');
        } catch (e) {}
        clearColdStartCallHandling();
      }
      
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : typeof e === 'string' ? e : 'Unknown error';
      console.error('[TotalConnectRTC] Failed to answer call:', e);
      localAnsweringCallIdRef.current = null;
      clearColdStartCallHandling();
      // Don't go back to idle immediately — show error on the ringing screen so user can retry
      setCallState('ringing');
      callStateRef.current = 'ringing';
      setError(errorMessage);
      notify.error(errorMessage || 'Failed to connect call. Try again or hang up.');
    }
  }, [incomingCall, callType, callSession, callNotifications, sipCallData, getLivekitToken, confirmColdStartHandled, clearColdStartCallHandling, markColdStartCallHandling, primeBrowserCallAudio, dispatchAnswerSignal]);
  
  // Ref for answerCall so WS handler can call latest version without stale closure
  const answerCallRef = useRef(answerCall);
  useEffect(() => { answerCallRef.current = answerCall; }, [answerCall]);

  const handleEnableInCallAudio = useCallback(async () => {
    await primeBrowserCallAudio();
    await callControls.enableAudio();
    setMediaAudioBlocked(false);
  }, [callControls, primeBrowserCallAudio]);
  
  const rejectCall = useCallback(async () => {
    if (callState !== 'ringing') return;
    
    localAnsweringCallIdRef.current = null;
    clearColdStartCallHandling();
    callNotifications.stopRingtone();
    callNotifications.cancelCallNotification(); // Dismiss Android/FCM notification
    callNotifications.hapticFeedback('heavy');
    
    // Clear ringing timeout
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    
    const currentCallId = callIdRef.current || incomingCall?.call_id;
    const isSipInboundCall = isSipCallRef.current || currentCallId?.startsWith('sip-inbound-');
    
    // Mark this call as handled to prevent re-ringing
    if (currentCallId) {
      lastHandledCallIdRef.current = currentCallId;
      // CRITICAL: Clear pending call answer to prevent cold start handler from re-triggering after reject
      coldStartHandledRef.current = currentCallId;
      confirmColdStartHandled(currentCallId);
      // Explicitly clear native SharedPreferences — call is truly done (rejected)
      try {
        (window as Window & { TotalAccessNative?: { clearPendingCall?: () => void } }).TotalAccessNative?.clearPendingCall?.();
      } catch (e) {}
      if (typeof window !== 'undefined') {
        const pendingAnswer = (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer;
        if (pendingAnswer && (pendingAnswer.callId === currentCallId || pendingAnswer.call_id === currentCallId)) {
          (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer = null;
        }
        try {
          localStorage.removeItem('coldstart_pending_call');
          sessionStorage.removeItem('pending_call_answer');
          localStorage.removeItem('coldstart_call_handling');
        } catch (e) {}
      }
    }
    
    if (wsClient.isConnected() && currentCallId) {
      wsClient.send({
        type: isSipInboundCall ? 'reject_sip_call' : 'reject_operations_call',
        call_id: currentCallId,
        call_log_id: sipCallLogIdRef.current
      });
    } else {
      try {
        const tokens = authService.getTokens();
        await fetch(buildApiUrl('/api/v1/calls'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'end',
            call_id: currentCallId,
            reason: 'rejected',
          }),
        });
      } catch (e) {
        console.error('[TotalConnectRTC] Failed to reject call:', e);
      }
    }
    
    setCallState('idle');
    callStateRef.current = 'idle';
    setViewMode('contacts');
    setSelectedUser(null);
    setSipCallData(null);
    setScreenPopData(null);
    isSipCallRef.current = false;
    isTransferTargetRef.current = false;
    sipCallLogIdRef.current = null;
    callIdRef.current = null;
    onClearIncomingCall?.();
  }, [callState, incomingCall, callNotifications, onClearIncomingCall, confirmColdStartHandled, clearColdStartCallHandling]);
  
  const endCall = useCallback(async (reason?: string) => {
    if (callStateRef.current === 'idle') return;

    localAnsweringCallIdRef.current = null;
    clearColdStartCallHandling();
    
    // Guard against spurious user_ended from HMR/stale closures:
    // If the call just connected (< 2s ago), user_ended is almost certainly not real
    if (reason === 'user_ended' && callStartTimeRef.current) {
      const connectedDuration = Date.now() - callStartTimeRef.current;
      if (connectedDuration < 2000) {
        console.warn('[TotalConnectRTC] Ignoring spurious user_ended - call only connected', connectedDuration, 'ms ago');
        return;
      }
    }
    
    // Mark this call as handled to prevent re-ringing
    const endingCallId = callIdRef.current;
    if (endingCallId) {
      lastHandledCallIdRef.current = endingCallId;
      // Add to window-level handled set (survives HMR)
      if (typeof window !== 'undefined') {
        if (!(window as Window & { __handledCallIds?: Set<string> }).__handledCallIds) {
          (window as Window & { __handledCallIds?: Set<string> }).__handledCallIds = new Set<string>();
        }
        (window as Window & { __handledCallIds?: Set<string> }).__handledCallIds?.add(endingCallId);
      }
      // Clear pending cold start data to prevent re-trigger
      coldStartHandledRef.current = endingCallId;
      confirmColdStartHandled(endingCallId);
      // Explicitly clear native SharedPreferences — call is truly done (ended)
      try {
        (window as Window & { TotalAccessNative?: { clearPendingCall?: () => void } }).TotalAccessNative?.clearPendingCall?.();
      } catch (e) {}
      if (typeof window !== 'undefined') {
        const pendingAnswer = (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer;
        if (pendingAnswer && (pendingAnswer.callId === endingCallId || pendingAnswer.call_id === endingCallId)) {
          (window as Window & { __pendingCallAnswer?: { callId?: string; call_id?: string } | null }).__pendingCallAnswer = null;
        }
        try {
          localStorage.removeItem('coldstart_pending_call');
          sessionStorage.removeItem('pending_call_answer');
          localStorage.removeItem('coldstart_call_handling');
        } catch (e) {}
      }
    }
    
    callNotifications.stopRingtone();
    callNotifications.stopDialTone();
    callNotifications.cancelCallNotification(); // Always cancel FCM notification when call ends
    resetOutgoingProgressState(false);
    if (roomRefSecondary.current?.state === ConnectionState.Connected) {
      callSessionSecondary.disconnectFromRoom(reason ?? 'user_ended');
    }
    callSession.disconnectFromRoom(reason);
    
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    
    // Clear ringing timeout
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    
    // Disable Tauri desktop call mode
    if (isTauri()) {
      disableCallMode();
    }
    
    // If SIP call, terminate via SIP API
    // Skip when another session/device owns the call; do not tear down the PSTN leg.
    // A pending transfer target must also never terminate the shared SIP leg
    // (timeout/cancel/consult hangup) — only once sip_transfer_finalized marks
    // them as the owner does terminateCall become correct.
    const skipSipTerminate = reason === 'answered_on_other_device' ||
                             reason === 'rejected_on_other_device' ||
                             reason === 'transferred_to_device' ||
                             reason === 'transfer_timeout' ||
                             reason === 'transfer_cancelled' ||
                             reason === 'sip_remote_hangup' ||
                             isTransferTargetRef.current;
    if (!skipSipTerminate && (sipCallData?.call_log_id || sipCallData?.provider_call_id || sipCallData?.livekit_room)) {
      const tokens = authService.getTokens();
      if (tokens?.access_token) {
        fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'terminateCall',
            call_id: sipCallData.call_log_id,
            client_id: sipCallData.client_id,
            provider_call_id: sipCallData.provider_call_id,
            livekit_room: sipCallData.livekit_room || callIdRef.current
          })
        }).catch(err => console.error('[TotalConnectRTC] SIP terminate error:', err));
      }
    }

    if (
      !skipSipTerminate &&
      (sipCallDataSecondary?.call_log_id ||
        sipCallDataSecondary?.provider_call_id ||
        sipCallDataSecondary?.livekit_room)
    ) {
      const tokensSec = authService.getTokens();
      if (tokensSec?.access_token) {
        fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokensSec.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'terminateCall',
            call_id: sipCallDataSecondary.call_log_id,
            client_id: sipCallDataSecondary.client_id,
            provider_call_id: sipCallDataSecondary.provider_call_id,
            livekit_room:
              sipCallDataSecondary.livekit_room || callIdRefSecondary.current,
          }),
        }).catch((err) =>
          console.error('[TotalConnectRTC] SIP terminate line 2 error:', err),
        );
      }
    }
    
    // Send end signal via WebSocket (skip for certain scenarios)
    const skipServerNotify = reason === 'answered_on_other_device' ||
                             reason === 'transferred_to_device' ||
                             reason === 'transfer_timeout' ||
                             reason === 'transfer_cancelled' ||
                             reason === 'ws_call_rejected_or_ended' ||
                             reason === 'ws_call_busy';
    
    if (wsClient.isConnected() && callIdRef.current && !skipServerNotify) {
      const isGroup = callIdRef.current.startsWith('group_call_');
      wsClient.send({
        type: isGroup ? 'leave_group_call' : 'end_operations_call',
        call_id: callIdRef.current,
        reason
      });
    }
    
    // Also call REST API for clean state
    // Skip when another device is handling the call
    try {
      const tokens = authService.getTokens();
      // Always try REST cleanup even for ws-originated endings,
      // to ensure video_meeting_participants.left_at is set
      if (callIdRef.current && !skipServerNotify) {
        await fetch(buildApiUrl('/api/v1/calls'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'end',
            call_id: callIdRef.current,
            reason,
          }),
        });
      }
    } catch (e) {
      console.error('[TotalConnectRTC] Failed to end call:', e);
    }
    
    // Reset all state
    setCallState('idle');
    callStateRef.current = 'idle'; // Sync ref immediately for async checks
    setViewMode(reason === 'user_cancelled' ? 'keypad' : 'contacts');
    setCallDuration(0);
    setSelectedUser(null);
    setSipCallData(null);
    setScreenPopData(null);
    setSipCallDataSecondary(null);
    setSelectedUserSecondary(null);
    setScreenPopDataSecondary(null);
    callIdRefSecondary.current = null;
    secondaryElapsedStartRef.current = null;
    setSecondLineIncoming(null);
    setFocusedLine('primary');
    setIsGroupCall(false);
    setGroupCallInfo(null);
    setActivityLog([]);
    setActiveReactions([]);
    setParticipantHandRaises(new Map());
    setWaitingParticipants([]);
    setShowWhiteboard(false);
    setWhiteboardElements([]);
    setWhiteboardUIMode({ kind: 'idle' });
    setRemotePresenterInfo(null);
    setIsRecording(false);
    setRecordingStartTime(null);
    setIsBreakoutActive(false);
    setBreakoutRooms([]);
    setCurrentBreakoutRoom(null);
    setShowBreakoutModal(false);
    setAddingParticipant(null);
    setShowInviteLink(false);
    setInviteLink(null);
    setGeneratingLink(false);
    setAttendedTransferState({ status: 'idle' });
    setPendingBlindTransfer({ active: false });
    pendingBlindTransferRef.current = false;
    pendingTransferTypeRef.current = null;
    isTransferTargetRef.current = false;
    setTransferringToExtension(null);
    setShowTransferDialog(false);
    setShowParkingDialog(false);
    setOutgoingCallProgress('idle');
    isSipCallRef.current = false;
    sipCallLogIdRef.current = null;
    callIdRef.current = null;
    callStartTimeRef.current = null;
    pendingConnectionRef.current = null;
    // Notify BiometricLockScreen to re-engage lock after call ends
    try {
      window.dispatchEvent(new CustomEvent('totalaccess-call-ended', { detail: { reason } }));
    } catch {}

    onClearIncomingCall?.();
  }, [
    callSession,
    callSessionSecondary,
    callNotifications,
    onClearIncomingCall,
    sipCallData,
    sipCallDataSecondary,
    disableCallMode,
    confirmColdStartHandled,
    clearColdStartCallHandling,
    resetOutgoingProgressState,
  ]);

  const hangUpFocusedLeg = useCallback(async () => {
    if (
      callStateRef.current === 'initiating' ||
      callStateRef.current === 'connecting'
    ) {
      await endCall('user_cancelled');
      return;
    }

    const secondaryLive =
      sipCallDataSecondary &&
      roomRefSecondary.current?.state === ConnectionState.Connected;
    const primaryLive =
      sipCallData &&
      roomRef.current?.state === ConnectionState.Connected;

    if (secondaryLive && focusedLine === 'secondary') {
      callSessionSecondary.disconnectFromRoom('user_ended');
      const tokens = authService.getTokens();
      if (tokens?.access_token && sipCallDataSecondary?.call_log_id) {
        fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'terminateCall',
            call_id: sipCallDataSecondary.call_log_id,
            client_id: sipCallDataSecondary.client_id,
            provider_call_id: sipCallDataSecondary.provider_call_id,
            livekit_room:
              sipCallDataSecondary.livekit_room || callIdRefSecondary.current,
          }),
        }).catch(() => {});
      }
      setSipCallDataSecondary(null);
      setSelectedUserSecondary(null);
      setScreenPopDataSecondary(null);
      callIdRefSecondary.current = null;
      secondaryElapsedStartRef.current = null;
      secondaryLineOccupiedRef.current = false;
      setFocusedLine('primary');
      setSecondLineIncoming(null);
      if (primaryLive) {
        await sipHoldMusic(false);
        await roomRef.current?.localParticipant.setMicrophoneEnabled(true);
        callControls.syncLocalParticipantUiFromRoom?.(roomRef.current, false);
      }
      return;
    }

    if (secondaryLive && primaryLive && focusedLine === 'primary') {
      callSession.disconnectFromRoom('user_ended');
      const tokens = authService.getTokens();
      if (tokens?.access_token && sipCallData?.call_log_id) {
        fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'terminateCall',
            call_id: sipCallData.call_log_id,
            client_id: sipCallData.client_id,
            provider_call_id: sipCallData.provider_call_id,
            livekit_room: sipCallData.livekit_room || callIdRef.current,
          }),
        }).catch(() => {});
      }
      setSipCallData(null);
      setScreenPopData(null);
      callIdRef.current = null;
      setFocusedLine('secondary');
      setSecondLineIncoming(null);
      await sipHoldMusicLeg(false, {
        call_log_id: sipCallDataSecondary?.call_log_id,
        livekit_room: sipCallDataSecondary?.livekit_room,
        client_id: sipCallDataSecondary?.client_id,
        callId: callIdRefSecondary.current,
      });
      await roomRefSecondary.current?.localParticipant.setMicrophoneEnabled(true);
      callControls.syncLocalParticipantUiFromRoom?.(
        roomRefSecondary.current,
        false,
      );
      return;
    }

    await endCall('user_ended');
  }, [
    endCall,
    sipCallData,
    sipCallDataSecondary,
    focusedLine,
    sipHoldMusic,
    sipHoldMusicLeg,
    callSession,
    callSessionSecondary,
    callControls,
  ]);

  // Sync endCallRef so WS handler always calls the latest version
  useEffect(() => { endCallRef.current = endCall; }, [endCall]);

  const totalConnectRtcWsCtxRef = useRef<TotalConnectRtcWsHandlerContext>(
    {} as TotalConnectRtcWsHandlerContext,
  );

  totalConnectRtcWsCtxRef.current = {
    userId: user?.id,
    callIdRef,
    callStateRef,
    sipCallLogIdRef,
    sipCallDataRef,
    localAnsweringCallIdRef,
    coldStartHandledRef,
    lastHandledCallIdRef,
    isOpenRef,
    isSipCallRef,
    isDNDEnabledRef,
    sipInboundMobileOnlyRef,
    pendingConnectionRef,
    ringTimeoutRef,
    callStartTimeRef,
    missedCallsCountRef,
    pendingBlindTransferRef,
    pendingTransferTypeRef,
    isTransferTargetRef,
    secondaryLineOccupiedRef,
    secondLinePendingRef,
    presentSecondLineIncomingRef,
    whiteboardUIModeRef,
    onClearIncomingCallRef,
    onIncomingCallRef,
    onMissedCallsChangeRef,
    callNotificationsRef,
    answerCallRef,
    endCallRef,
    leaveRoomAfterTransferRef,
    sipHoldMusicRef,
    checkIfBusyRef,
    callSession,
    callControls,
    roomRef,
    setSipCallData,
    setIsGateCall,
    setCallQueue,
    setCallState,
    setOutgoingCallProgress: setOutgoingProgressState,
    setViewMode,
    setSelectedUser,
    setCallType,
    setCallDuration,
    setPickupGroupCalls,
    setShowPickupIndicator,
    setPickingUpCall,
    setPendingBlindTransfer,
    setAttendedTransferState,
    setTransferringToExtension,
    setSipParticipantLocalMute,
    setShowTransferDialog,
    setWhiteboardElements,
    setRemotePresenterInfo,
    setWhiteboardUIMode,
    setShowWhiteboard,
    setBreakoutRooms,
    setIsBreakoutActive,
    setCurrentBreakoutRoom,
    setIsGroupCall,
    setGroupCallInfo,
    setParticipantHandRaises,
    setActiveReactions,
    setWaitingParticipants,
    setScreenPopData,
    markColdStartCallHandling,
    clearColdStartCallHandling,
    fetchScreenPopData,
    getLivekitToken,
    sendWhiteboardStatus,
    addActivityEntry,
    loadParkingSlots,
    pickingUpCall,
    showParkingDialog,
    transferringToExtension,
    isTauriDesktop,
    focusWindow,
  };

  useEffect(() => {
    if (callState !== 'initiating' || !sipCallData || !callSession.isConnected) {
      return;
    }

    if (callSession.participants.length === 0) {
      return;
    }

    // A SIP bridge leg joins the room as soon as the provider accepts the
    // INVITE — the callee may still be ringing. Only the leg reporting
    // sip.callStatus 'active' means the callee actually answered. Legs
    // without the attribute keep the legacy "join = answered" behavior.
    const hasAnswered = callSession.participants.some(
      (p) => !isSipParticipantIdentity(p.identity) || !isSipLegStillRinging(p.attributes)
    );

    if (!hasAnswered) {
      if (outgoingCallProgress !== 'ringing') {
        setOutgoingProgressState('ringing');
      }
      return;
    }

    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }

    callNotifications.stopDialTone();
    setOutgoingCallProgress('idle');
    setCallState('connected');
    callStateRef.current = 'connected';
    callStartTimeRef.current = callStartTimeRef.current || Date.now();
  }, [callNotifications, callSession.isConnected, callSession.participants, callState, sipCallData, outgoingCallProgress, setOutgoingProgressState]);
  
  // ============================================
  // Effects
  // ============================================
  
  // Fetch when Calls UI is open, or whenever embedded in the standalone suite (warm keypad/contacts).
  useEffect(() => {
    if (!user) return;
    if (!(isOpen || layoutVariant === 'embedded')) return;
    fetchOperationsUsers();
    fetchCallLogs();
    fetchCallSettings();
    loadExtensions();
  }, [isOpen, layoutVariant, user, fetchOperationsUsers, fetchCallLogs, fetchCallSettings, loadExtensions]);

  // Re-fetch call logs, call settings, contacts, and extensions when the
  // selected PABX client changes (multi-client selector) or when the recents
  // "All clients" toggle changes. This keeps the UI in sync with the selected
  // calling context — contacts now follow the selected client so users can
  // call team members of any client they have total-connect access to.
  useEffect(() => {
    if (!user) return;
    if (!(isOpen || layoutVariant === 'embedded')) return;
    // Only re-fetch if there's an actual selection (skip initial mount — the
    // effect above already handles the initial fetch).
    if (selectedPabxClientId == null && !recentsAllClients) return;
    fetchOperationsUsers();
    fetchCallLogs();
    fetchCallSettings();
    loadExtensions();
  }, [selectedPabxClientId, recentsAllClients, isOpen, layoutVariant, user, fetchOperationsUsers, fetchCallLogs, fetchCallSettings, loadExtensions]);

  // ============================================
  // Room participant monitor — auto-end 1:1 calls when remote party disconnects
  // LiveKit SIP gateway participants may not always fire ParticipantDisconnected
  // cleanly when the external caller hangs up, so we poll as a fallback.
  // ============================================
  const emptyRoomChecksRef = useRef(0);
  useEffect(() => {
    if (callState !== 'connected' || isGroupCall) {
      emptyRoomChecksRef.current = 0;
      return;
    }

    const interval = setInterval(() => {
      if (!roomRef.current || callStateRef.current !== 'connected') {
        emptyRoomChecksRef.current = 0;
        return;
      }

      const remoteCount = roomRef.current.remoteParticipants.size;
      if (remoteCount === 0) {
        emptyRoomChecksRef.current++;
        if (emptyRoomChecksRef.current >= 2) {
          toast('Call ended — remote party disconnected', { icon: '📞' });
          endCall('remote_participant_gone');
        }
      } else {
        emptyRoomChecksRef.current = 0;
      }
    }, 3000);

    return () => {
      clearInterval(interval);
      emptyRoomChecksRef.current = 0;
    };
  }, [callState, isGroupCall, endCall]);

  type LegacyIncomingCall = IncomingCall & {
    callId?: string;
    callType?: CallType;
  };

  type AnswerSignalPayload = {
    type: 'answer_sip_call' | 'answer_operations_call';
    call_id: string;
    call_log_id?: string | null;
  };

  type LocalParticipantDtmfCapable = {
    publishDtmf?: (code: number, digit: string) => void;
  };

  type ColdStartDebugWindow = Window & {
    __coldStartLog?: (scope: string, message: string, detail?: string) => void;
    __handledCallIds?: Set<string>;
    __hideCallConnectingUI?: () => void;
    __shipColdStartLogs?: (payload: { trigger: string; forceAll?: boolean }) => void;
  };

  // ============================================
  // CRITICAL: Handle Capacitor cold start calls
  // ============================================
  useEffect(() => {
    const getColdStartDebugWindow = (): ColdStartDebugWindow | null => {
      if (typeof window === 'undefined') {
        return null;
      }

      return window as ColdStartDebugWindow;
    };

    const coldStartWindow = getColdStartDebugWindow();
    const csLog = coldStartWindow?.__coldStartLog;
    
    if (!coldStartPendingCall) return;
    if (coldStartHandledRef.current === coldStartPendingCall.callId) return;
    if (callState !== 'idle') {
      csLog?.('REACT_COLDSTART', 'Skipping - callState not idle', JSON.stringify({ callState, callId: coldStartPendingCall.callId }));
      return;
    }
    
    // STALE SESSION GUARD: If this component has been alive for > 30 seconds,
    // this is NOT a true cold start — it's a long-lived session that received
    // a new call via native event injection. The page is likely about to reload.
    // If we process the call here, the old session will consume the one-time WS
    // sipCallData message, and the new session after reload will never get it.
    // Let the NEW session handle the call instead.
    const sessionAge = Date.now() - componentMountTimeRef.current;
    // WebView sessions can legitimately stay alive while native delivers a NEW answered-call intent.
    // Only defer stale-session handling when the pending payload itself looks stale by timestamp.
    const pendingTsRaw = coldStartPendingCall.timestamp as string | number | undefined;
    const pendingTimestampMs =
      typeof pendingTsRaw === 'number' && Number.isFinite(pendingTsRaw)
        ? pendingTsRaw
        : typeof pendingTsRaw === 'string'
          ? Date.parse(pendingTsRaw)
          : NaN;
    const pendingAgeMs =
      Number.isFinite(pendingTimestampMs) && pendingTimestampMs > 0
        ? Date.now() - pendingTimestampMs
        : 0;
    const pendingLooksStale =
      pendingAgeMs > 120000 || (!pendingTsRaw && sessionAge > 120000);

    if (sessionAge > 30000 && pendingLooksStale) {
      csLog?.('REACT_COLDSTART', 'Skipping - stale session (age: ' + sessionAge + 'ms), deferring to new session', JSON.stringify({ callId: coldStartPendingCall.callId }));
      return;
    }

    if (localAnsweringCallIdRef.current === coldStartPendingCall.callId) {
      csLog?.('REACT_COLDSTART', 'Skipping - answer already in progress', coldStartPendingCall.callId);
      return;
    }
    
    // Window-level dedup that survives HMR (refs get reset on Fast Refresh)
    const win = getColdStartDebugWindow();
    const handledSet: Set<string> = win?.__handledCallIds || new Set();
    if (handledSet.has(coldStartPendingCall.callId)) {
      csLog?.('REACT_COLDSTART', 'Skipping - in window handled set', coldStartPendingCall.callId);
      coldStartHandledRef.current = coldStartPendingCall.callId;
      confirmColdStartHandled(coldStartPendingCall.callId);
      return;
    }
    
    // Check if this call was already handled via the regular WS flow or answer/reject
    if (lastHandledCallIdRef.current === coldStartPendingCall.callId) {
      csLog?.('REACT_COLDSTART', 'Skipping - already handled via WS', coldStartPendingCall.callId);
      coldStartHandledRef.current = coldStartPendingCall.callId;
      confirmColdStartHandled(coldStartPendingCall.callId);
      return;
    }
    
    csLog?.('REACT_COLDSTART', 'Processing cold start call', JSON.stringify({ callId: coldStartPendingCall.callId, autoAnswer: coldStartPendingCall.autoAnswer, isSip: coldStartPendingCall.callId.startsWith('sip-inbound-') }));
    
    coldStartHandledRef.current = coldStartPendingCall.callId;
    markColdStartCallHandling(coldStartPendingCall.callId);
    
    setCallType(coldStartPendingCall.callType || 'voice');
    setSelectedUser({
      id: typeof coldStartPendingCall.callerId === 'number' 
        ? coldStartPendingCall.callerId 
        : parseInt(coldStartPendingCall.callerId as string) || 0,
      email: '',
      first_name: (coldStartPendingCall.callerName || 'Unknown').split(' ')[0] || '',
      last_name: (coldStartPendingCall.callerName || '').split(' ').slice(1).join(' ') || '',
      role: '',
      module_role: '',
      avatar_url: coldStartPendingCall.callerAvatar,
    });
    callIdRef.current = coldStartPendingCall.callId;

    if (!isOpenRef.current) {
      onEnsureCallModalOpen?.();
    }

    // DO NOT call confirmColdStartHandled here!
    // The page may reload during cold start (WebView loads 3+ times),
    // and clearing SharedPreferences here makes the call invisible on subsequent loads.
    // confirmColdStartHandled will be called when the call is actually answered (answerCall)
    // or ended (endCall). The coldStartHandledRef prevents same-page re-processing.
    
    // SIP calls should use the regular answerCall flow which handles pre-gen tokens
    const isSipColdStart = coldStartPendingCall.callId.startsWith('sip-inbound-');
    
    if (coldStartPendingCall.autoAnswer) {
      csLog?.('REACT_COLDSTART', 'Auto-answering', JSON.stringify({ isSip: isSipColdStart, callId: coldStartPendingCall.callId }));
      
      if (isSipColdStart) {
        // For SIP cold start calls, set ringing state then proactively auto-answer.
        // Previously we waited passively for the WS sip_incoming_call message to
        // provide sipCallData, but that message may have been consumed by a stale
        // session before page reload. answerCall() has a fallback chain:
        //   1. Use pre-gen token from sipCallData (if available)
        //   2. Poll sipCallDataRef for 3 seconds (WS may deliver it)
        //   3. Fall back to REST API getLivekitToken(roomName)
        isSipCallRef.current = true;
        setCallState('ringing');
        callStateRef.current = 'ringing';
        setViewMode('incall');
        
        // Check for cached sipCallData from a previous/stale session
        try {
          const cached = localStorage.getItem('sip_call_data_cache');
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.call_id === coldStartPendingCall.callId && Date.now() - parsed.timestamp < 60000) {
              csLog?.('REACT_COLDSTART', 'Found cached sipCallData from previous session', coldStartPendingCall.callId);
              setSipCallData(parsed.sip_data);
              sipCallDataRef.current = parsed.sip_data;
              sipCallLogIdRef.current = parsed.sip_data?.call_log_id || null;
              localStorage.removeItem('sip_call_data_cache');
            }
          }
        } catch (e) {}
        
        csLog?.('REACT_COLDSTART', 'SIP cold start - set ringing, will auto-answer via answerCall()', coldStartPendingCall.callId);
        // User already tapped "Answer" on the FCM notification — stop native ring immediately.
        // Do NOT play in-app ring since we're about to auto-answer momentarily.
        callNotifications.cancelCallNotification();
        
        // Proactively auto-answer after a brief delay.
        // answerCall() handles the full token fallback chain.
        const sipColdStartCallId = coldStartPendingCall.callId;
        setTimeout(() => {
          if (callStateRef.current === 'ringing' && callIdRef.current === sipColdStartCallId) {
            csLog?.('REACT_COLDSTART', 'Calling answerCall() for SIP cold start', sipColdStartCallId);
            answerCallRef.current();
          }
        }, 450); // brief delay so sipCallData WS / cache can win; answerCall still polls/falls back
        
        scheduleDismissNativeCallConnectingOverlay();
        
        // SAFETY NET: If the SIP call stays in 'ringing' for more than 30s,
        // answerCall() also failed. Clean up so the user isn't stuck.
        setTimeout(() => {
          if (callStateRef.current === 'ringing' && callIdRef.current === sipColdStartCallId) {
            console.warn('[TotalConnectRTC] 📞 SIP cold start ringing timeout - cleaning up after 30s');
            csLog?.('REACT_COLDSTART', 'SIP ringing TIMEOUT after 30s - cleaning up', sipColdStartCallId);
            callNotificationsRef.current?.stopRingtone();
            callNotificationsRef.current?.cancelCallNotification();
            setCallState('idle');
            callStateRef.current = 'idle';
            localAnsweringCallIdRef.current = null;
            clearColdStartCallHandling();
            setViewMode('contacts');
            setSelectedUser(null);
            callIdRef.current = null;
            setSipCallData(null);
            isSipCallRef.current = false;
            onClearIncomingCallRef.current?.();
            confirmColdStartHandled(sipColdStartCallId);
            // Hide startup overlay if still showing
            getColdStartDebugWindow()?.__hideCallConnectingUI?.();
            win?.__shipColdStartLogs?.({ trigger: 'sip_coldstart_timeout', forceAll: true });
          }
        }, 30000);
        
        // Ship logs after SIP cold-start setup
        setTimeout(() => win?.__shipColdStartLogs?.({ trigger: 'sip_coldstart', forceAll: true }), 5000);
      } else {
        // Regular (non-SIP) cold start auto-answer
        setCallState('connecting');
        callStateRef.current = 'connecting';
        setViewMode('incall');
        callStartTimeRef.current = Date.now();

        scheduleDismissNativeCallConnectingOverlay();

        (async () => {
          try {
            const tokens = authService.getTokens();
            const res = await fetch(buildApiUrl('/api/v1/calls'), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${tokens?.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                action: 'answer',
                call_id: coldStartPendingCall.callId,
              }),
            });
            
            const data = await res.json();
            if (data.success) {
              const connected = await callSession.connectToRoom(
                data.call.livekit_room, 
                coldStartPendingCall.callType || 'voice',
                data.call.livekit_token,
                data.call.livekit_host
              );
              if (connected) {
                setCallState('connected');
                callStateRef.current = 'connected';
                csLog?.('REACT_COLDSTART', 'Call connected successfully', coldStartPendingCall.callId);
              } else {
                throw new Error('Failed to connect to room');
              }
            } else {
              throw new Error(data.error || 'Failed to answer call');
            }
          } catch (e: unknown) {
            const errorMessage = getErrorMessage(e);
            console.error('[TotalConnectRTC] 📞 Cold start call failed:', e);
            csLog?.('REACT_COLDSTART', 'Cold start call FAILED', JSON.stringify({ callId: coldStartPendingCall.callId, error: errorMessage }));
            setCallState('idle');
            callStateRef.current = 'idle';
            localAnsweringCallIdRef.current = null;
            clearColdStartCallHandling();
            setError(errorMessage);
            notify.error(errorMessage || 'Failed to connect to call');
          }
          // Ship logs after cold-start call resolves (success or failure)
          setTimeout(() => getColdStartDebugWindow()?.__shipColdStartLogs?.({ trigger: 'coldstart_resolved', forceAll: true }), 2000);
        })();
      }
    } else {
      csLog?.('REACT_COLDSTART', 'Showing ringing UI (no auto-answer)', JSON.stringify({ isSip: isSipColdStart, callId: coldStartPendingCall.callId }));
      if (isSipColdStart) isSipCallRef.current = true;
      setCallState('ringing');
      callStateRef.current = 'ringing';
      setViewMode('incall');
      callNotifications.playRingtone();

      scheduleDismissNativeCallConnectingOverlay();
      
      // Ringing timeout safety net for cold start calls (same as WS handler)
      if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current === 'ringing') {
          csLog?.('REACT_COLDSTART', 'Ringing TIMEOUT after 50s', coldStartPendingCall.callId);
          callNotificationsRef.current?.stopRingtone();
          callNotificationsRef.current?.stopDialTone();
          callNotificationsRef.current?.cancelCallNotification();
          setCallState('idle');
          callStateRef.current = 'idle';
          localAnsweringCallIdRef.current = null;
          clearColdStartCallHandling();
          setViewMode('contacts');
          setSelectedUser(null);
          callIdRef.current = null;
          onClearIncomingCallRef.current?.();
          confirmColdStartHandled(coldStartPendingCall.callId);
          getColdStartDebugWindow()?.__hideCallConnectingUI?.();
        }
      }, 50000);
      
      // Ship logs after ringing UI shown
      setTimeout(() => win?.__shipColdStartLogs?.({ trigger: 'coldstart_ringing', forceAll: true }), 5000);
    }
  }, [coldStartPendingCall, callState, callSession, callNotifications, confirmColdStartHandled, clearColdStartCallHandling, markColdStartCallHandling, onEnsureCallModalOpen]);
  
  // ============================================
  // WebSocket Connection for Call Signaling
  // IMPORTANT: Connects even when modal is closed to receive incoming calls
  // ============================================
  // Intentionally scoped to the signed-in user only so the shared realtime listeners
  // are not repeatedly torn down during normal call UI state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id) return;
    
    const handleWebSocketMessage = (raw: unknown) =>
      handleTotalConnectRtcWebSocketMessage(raw, totalConnectRtcWsCtxRef.current);
    
    // Use the singleton wsClient for call signaling instead of creating a raw WebSocket.
    // wsClient handles connection, reconnection, and lifecycle automatically.
    // We register for legacy (non-channel) messages since call signaling hasn't been
    // migrated to the channel system yet.
    
    // Ensure wsClient is connected
    wsClient.connect().then(() => {
      wsReconnectPendingRef.current = false;
      
      // Send any queued answer message — only if actually connected
      // (_connect() silently returns when app is in background on Capacitor)
      if (pendingAnswerMessageRef.current && wsClient.isConnected()) {
        wsClient.send(pendingAnswerMessageRef.current);
        pendingAnswerMessageRef.current = null;
      }
      
      if (wsClient.isConnected() && document.visibilityState === 'visible') {
        wsClient.send({
          type: 'app_visibility',
          is_foreground: true
        });
      }
    }).catch(err => {
      if (!isIntentionalDisconnectError(err)) {
        console.warn('[TotalConnectRTC] wsClient connect failed (will auto-retry):', err);
      }
    });
    
    // Subscribe to legacy messages for call signaling
    const unsubLegacy = wsClient.onLegacyMessage(handleWebSocketMessage);
    
    // Also send queued messages when wsClient reconnects
    const unsubConnection = wsClient.onConnection((status) => {
      if (status === 'connected') {
        if (pendingAnswerMessageRef.current) {
          wsClient.send(pendingAnswerMessageRef.current);
          pendingAnswerMessageRef.current = null;
        }
      }
    });
    
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      
      if (wsClient.isConnected()) {
        wsClient.send({
          type: 'app_visibility',
          is_foreground: isVisible
        });
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // CRITICAL: Listen for incomingCallAnswered event DIRECTLY
    // This fires when user taps "Answer" on FCM notification.
    // By listening here (not through Header → prop → useEffect chain),
    // we can auto-answer immediately without multiple React render cycles.
    let directAnsweredCallId: string | null = null; // Dedup within this listener
    const handleDirectCallAnswer = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.call_id) return;
      
      const eventCallId = detail.call_id;
      
      // Skip duplicates (Android fires this event twice)
      if (directAnsweredCallId === eventCallId) {
        return;
      }
      
      // Only auto-answer if we're currently ringing for this exact call
      if (callStateRef.current === 'ringing' && callIdRef.current === eventCallId) {
        directAnsweredCallId = eventCallId;
        answerCallRef.current();
      } else {
      }
    };
    
    window.addEventListener('incomingCallAnswered', handleDirectCallAnswer);

    const handleForegroundIncomingPush = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail || typeof detail !== 'object') return;
      void handleTotalConnectRtcWebSocketMessage(detail, totalConnectRtcWsCtxRef.current);
    };
    window.addEventListener('totalaccess_foreground_incoming_call', handleForegroundIncomingPush);
    
    // Listen for incomingCallDeclined event from FCM notification "Decline" button
    // This is a safety net: if the buffered WS incoming_call message was processed
    // before the __declinedCallIds flag was set, stop ringing here.
    const handleDirectCallDecline = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const d = (detail || {}) as Record<string, unknown>;
      const declinedCallId = d.call_id as string | undefined;
      const rejCallId = String(d.call_id ?? callIdRef.current ?? '');
      const rawLogId = d.call_log_id ?? d.sip_call_log_id;
      const callLogId =
        rawLogId === undefined || rawLogId === null
          ? undefined
          : String(rawLogId);

      addDeclinedIncomingCallIds([rejCallId || null, callLogId ?? null]);

      const sendRejectToServer = () => {
        if (!rejCallId) return;
        try {
          const ct = d.call_type;
          const isSipFlag =
            d.is_sip_call === true ||
            d.is_sip_call === 'true';
          const isSipInbound =
            isSipFlag ||
            ct === 'sip_inbound' ||
            rejCallId.startsWith('sip-inbound-');
          if (isSipInbound) {
            wsClient.send({
              type: 'reject_sip_call',
              call_id: rejCallId,
              ...(callLogId ? { call_log_id: callLogId } : {}),
            });
          } else {
            wsClient.send({
              type: 'reject_operations_call',
              call_id: rejCallId,
            });
          }
        } catch (e) {
          console.error('[TotalConnectRTC] Error sending reject:', e);
        }
      };

      if (callStateRef.current === 'ringing') {
        // If we're ringing for this specific call (or any call when no ID match),
        // stop ringing and clean up
        if (!declinedCallId || callIdRef.current === declinedCallId) {
          callNotificationsRef.current?.stopRingtone();
          callNotificationsRef.current?.stopDialTone();
          callNotificationsRef.current?.cancelCallNotification();
          if (ringTimeoutRef.current) { clearTimeout(ringTimeoutRef.current); ringTimeoutRef.current = null; }

          sendRejectToServer();

          setCallState('idle');
          callStateRef.current = 'idle';
          setViewMode('contacts');
          setSelectedUser(null);
          callIdRef.current = null;
          onClearIncomingCallRef.current?.();
        }
        return;
      }

      // Declined from notification before ringing UI updated — still reject and
      // flag IDs so a trailing WS / foreground envelope cannot open RingingUI.
      sendRejectToServer();
    };
    
    window.addEventListener('incomingCallDeclined', handleDirectCallDecline);
    
    return () => {
      unsubLegacy();
      unsubConnection();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('incomingCallAnswered', handleDirectCallAnswer);
      window.removeEventListener('totalaccess_foreground_incoming_call', handleForegroundIncomingPush);
      window.removeEventListener('incomingCallDeclined', handleDirectCallDecline);
      // Do NOT disconnect wsClient here — it's a singleton shared across the app
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  
  // Handle incoming call prop (from parent component / WebSocket)
  useEffect(() => {
    if (!incomingCall) return;

    const normalizedIncomingCall: LegacyIncomingCall = incomingCall;
    
    // Normalize call_id: Header passes 'callId' (camelCase), IncomingCall uses 'call_id' (snake_case)
    const callId = normalizedIncomingCall.call_id || normalizedIncomingCall.callId;
    if (!callId) {
      return;
    }
    
    // If user already tapped "Answer" and the WS handler set us to ringing,
    // auto-answer immediately (this prop carries the autoAnswer flag from Header)
    if (normalizedIncomingCall.autoAnswer && callStateRef.current === 'ringing' && callIdRef.current === callId) {
      answerCall();
      onClearIncomingCall?.();
      return;
    }
    
    if (callState !== 'idle') return;
    
    if (coldStartHandledRef.current === callId) {
      onClearIncomingCall?.();
      return;
    }
    
    // Prevent re-ringing for a call we just finished handling
    if (lastHandledCallIdRef.current === callId) {
      onClearIncomingCall?.();
      return;
    }
    
    // If the WS handler already set us to ringing/connecting/connected for this call,
    // don't re-process from the prop. This prevents the prop handler from overriding
    // the WS-driven state. (autoAnswer is handled above before the idle check)
    if (callIdRef.current === callId && callStateRef.current !== 'idle') {
      return;
    }
    
    // Set SIP state if this is a SIP call
    const isSipInbound = incomingCall.isSipCall || callId?.startsWith('sip-inbound-');
    if (isSipInbound && incomingCall.sipData) {
      const normalizedSipData = {
        ...incomingCall.sipData,
        call_log_id: incomingCall.sipData.call_log_id,
        livekit_room: incomingCall.sipData.livekit_room || callId,
        livekit_token: incomingCall.sipData.livekit_token,
        livekit_host: incomingCall.sipData.livekit_host,
        caller_number: incomingCall.caller_number || incomingCall.sipData.caller_number,
        caller_name: incomingCall.caller_name || incomingCall.sipData.caller_name,
      };
      isSipCallRef.current = true;
      sipCallLogIdRef.current = normalizedSipData.call_log_id || null;
      setSipCallData(normalizedSipData);
      
      // Fetch screen pop for SIP calls — but skip for test calls (caller_number
      // like "PABX-Test" has no real phone digits and would match every contact
      // in the database via the LIKE '%...' fallback query).
      const isTestCallNumber = /^pabx[- ]?test$/i.test(normalizedSipData.caller_number || '');
      if (normalizedSipData.caller_number && !isTestCallNumber) {
        fetchScreenPopData(normalizedSipData.caller_number);
      }
    }

    const sipCallerName =
      incomingCall.sipData?.caller_name || incomingCall.caller_name || 'Incoming Call';
    const sipCallerNumber =
      incomingCall.caller_number || incomingCall.sipData?.caller_number || '';
    
    setCallState('ringing');
    callStateRef.current = 'ringing';
    setCallType(normalizedIncomingCall.call_type || normalizedIncomingCall.callType || 'voice');
    callIdRef.current = callId;
    setSelectedUser({
      id: incomingCall.caller_id,
      email: '',
      first_name: isSipInbound ? sipCallerName : (incomingCall.caller_name?.split(' ')[0] || ''),
      last_name:
        isSipInbound
          ? sipCallerNumber && sipCallerNumber !== sipCallerName
            ? sipCallerNumber
            : ''
          : (incomingCall.caller_name?.split(' ').slice(1).join(' ') || ''),
      role: isSipInbound ? 'External' : '',
      module_role: '',
      avatar_url: incomingCall.caller_avatar,
    });
    setViewMode('incall');
    callNotifications.playRingtone();

    // Dispatch DOM event so BiometricLockScreen auto-unlocks for incoming calls
    try {
      window.dispatchEvent(new CustomEvent('totalaccess-incoming-call', { detail: incomingCall }));
    } catch {}
  }, [incomingCall, callState, callNotifications, fetchScreenPopData, onClearIncomingCall, answerCall]);
  
  // Process call queue - when current call ends and we have queued calls, present them
  useEffect(() => {
    if (callState === 'idle' && callQueue.length > 0) {
      // Small delay to let state settle before presenting next call
      const timer = setTimeout(() => {
        if (callStateRef.current !== 'idle') return; // Re-check after delay
        const nextCall = callQueue[0];
        setCallQueue(prev => prev.slice(1));
        
        // Present the queued call as incoming
        onIncomingCall?.(nextCall);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [callState, callQueue, onIncomingCall]);

  // Gate-call flag is per-call — clear it once the line returns to idle so a
  // subsequent ordinary call doesn't inherit authorize/deny controls.
  useEffect(() => {
    if (callState === 'idle') setIsGateCall(false);
  }, [callState]);
  
  // Call duration timer - compute from elapsed time for Capacitor resilience
  // (setInterval can be throttled by mobile WebViews). Dual-line: timer follows focused leg.
  useEffect(() => {
    if (callState !== 'connected') {
      return;
    }

    const startForFocusedLeg = () =>
      focusedLine === 'secondary' && secondaryElapsedStartRef.current != null
        ? secondaryElapsedStartRef.current
        : callStartTimeRef.current ?? Date.now();

    const tick = () => {
      setCallDuration(
        Math.floor((Date.now() - startForFocusedLeg()) / 1000),
      );
    };

    tick();
    callTimerRef.current = setInterval(tick, 1000);

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
    };
  }, [callState, focusedLine]);
  
  // ============================================
  // Close Handler
  // ============================================
  const handleClose = useCallback(() => {
    if (callState !== 'idle') {
      return;
    }
    onClose();
  }, [callState, onClose]);

  // Join from the Meetings tab — internal room links navigate in-app (the widget
  // must close so it doesn't overlay the join page); external links open a tab.
  const handleJoinMeetingTarget = useCallback(
    (target: MeetingJoinTarget) => {
      if (target.kind === 'internal') {
        router.push(target.path);
        handleClose();
      } else {
        window.open(target.url, '_blank', 'noopener,noreferrer');
      }
    },
    [router, handleClose],
  );

  const handleOpenMeetingLink = useCallback(
    (url: string) => {
      const target = resolveMeetingJoinTarget(url);
      handleJoinMeetingTarget(target ?? { kind: 'external', url });
    },
    [handleJoinMeetingTarget],
  );
  
  // Meeting overlays must render in EVERY branch below — an early return that
  // drops them would unmount the meeting iframe and kill the live room.
  const meetingOverlays = (
    <>
      {/* Schedule meeting — same EventModal the Meetings tab uses */}
      {showScheduleMeetingModal && (
        <EventModal
          event={null}
          selectedDate={new Date()}
          isEditing={true}
          readOnly={false}
          teams={calendarTeams}
          isTeamCalendarView={false}
          presentation="slideover"
          onClose={() => setShowScheduleMeetingModal(false)}
          onEdit={() => {}}
          onDelete={() => {}}
          onSave={handleScheduleMeetingSave}
        />
      )}

      {/* Full-screen in-window meeting room (web) — same as EmbeddedCalls */}
      {callRoomUrl && (
        <div
          className="fixed inset-0 z-[110] bg-gray-950 flex flex-col"
          style={{
            paddingTop: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            paddingRight: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
            paddingBottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
            paddingLeft: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
          }}
        >
          <iframe
            src={callRoomUrl}
            title="Meeting room"
            className="flex-1 w-full border-0"
            allow="camera; microphone; display-capture; autoplay; fullscreen; clipboard-write"
            allowFullScreen
          />
          <button
            onClick={requestCloseCallRoomModal}
            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors"
            style={{
              top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
              right: 'calc(env(safe-area-inset-right, 0px) + 0.75rem)',
            }}
            title="Close meeting"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );

  // ============================================
  // Render: Not Open (still paint ringing/connecting when modal closed — cold start / native intent)
  // ============================================
  // meetingOverlays must stay at child index 1 — every other render branch is
  // <>{UI}{meetingOverlays}</>. Returning it bare here moves it to the root so
  // opening/closing the widget would remount the meeting iframe and kill the
  // live room (beforeunload → disconnect + a fresh doc that dup-joins).
  if (!isOpen && callState === 'idle') {
    return (
      <>
        {null}
        {meetingOverlays}
      </>
    );
  }
  
  // ============================================
  // Render: Ringing State
  // ============================================
  if (callState === 'ringing') {
    return (
      <>
      <RingingUI
        callType={callType}
        selectedUser={selectedUser}
        screenPopData={screenPopData}
        sipCallData={sipCallData}
        isGroupCall={isGroupCall}
        groupCallInfo={groupCallInfo}
        onAnswer={answerCall}
        onReject={rejectCall}
        onRemindMe={handleRemindMe}
        onQuickMessage={handleQuickMessage}
        onHapticFeedback={callNotifications.hapticFeedback}
        isDark={isDark}
        primaryColor={primaryColor}
      />
      {meetingOverlays}
      </>
    );
  }

  // ============================================
  // Render: In-Call State
  // ============================================
  if (viewMode === 'incall' || callState === 'initiating' || callState === 'connecting' || callState === 'connected') {
    const displaySelectedUser =
      focusedLine === 'secondary' && selectedUserSecondary
        ? selectedUserSecondary
        : selectedUser;
    const displayScreenPop =
      focusedLine === 'secondary'
        ? screenPopDataSecondary ?? screenPopData
        : screenPopData;
    const displaySip =
      focusedLine === 'secondary' && sipCallDataSecondary
        ? sipCallDataSecondary
        : sipCallData;
    const displayParticipants =
      focusedLine === 'secondary'
        ? callSessionSecondary.participants
        : callSession.participants;
    const displayRoomRef =
      focusedLine === 'secondary' ? roomRefSecondary : roomRef;
    const displayLocalTracksRef =
      focusedLine === 'secondary' ? localTracksRefSecondary : localTracksRef;

    return (
      <>
      <InCallUI
        callType={callType}
        callState={callState}
        callDuration={callDuration}
        outgoingCallProgress={outgoingCallProgress}
        selectedUser={displaySelectedUser}
        screenPopData={displayScreenPop}
        sipCallData={displaySip}
        isGroupCall={isGroupCall}
        groupCallInfo={groupCallInfo}
        participants={displayParticipants}
        localTracksRef={displayLocalTracksRef}
        roomRef={displayRoomRef}
        remoteVideoRefs={remoteVideoRefs}
        localVideoRef={localVideoRef}
        isMuted={callControls.isMuted}
        isVideoEnabled={callControls.isVideoEnabled}
        isSpeakerOn={callControls.isSpeakerOn}
        canToggleSpeaker={callControls.canToggleSpeaker}
        isOnHold={callControls.isOnHold}
        isBlurEnabled={callControls.isBlurEnabled}
        isScreenSharing={callControls.isScreenSharing}
        isHandRaised={callControls.isHandRaised}
        isRecording={isRecording}
        recordingStartTime={recordingStartTime}
        remoteScreenShareActive={false}
        remoteVideoActive={remoteVideoActive}
        isFrontCamera={callControls.isFrontCamera}
        audioBlocked={callNotifications.audioBlocked || mediaAudioBlocked}
        onToggleMute={callControls.toggleMute}
        onToggleVideo={callControls.toggleVideo}
        onToggleSpeaker={callControls.toggleSpeaker}
        onToggleHold={callControls.toggleHold}
        onToggleBlur={callControls.toggleBlur}
        onToggleScreenShare={callControls.toggleScreenShare}
        onToggleHandRaise={callControls.toggleHandRaise}
        onToggleRecording={toggleRecording}
        onSwitchCamera={callControls.switchCamera}
        onSendReaction={callControls.sendReaction}
        onEnableAudio={handleEnableInCallAudio}
        onEndCall={() =>
          hangUpFocusedLeg()
        }
        onPopOut={() => {
          // Pop-out currently only supported on web/desktop (not Capacitor)
          if (isCapacitor()) {
            toast('Pop-out not available on mobile');
            return;
          }
          if (!callIdRef.current) return;
          popOutToWindow();
        }}
        onTransfer={(ext, name) => {
          startAttendedTransfer(ext, name);
        }}
        onParkCall={(slot) => parkCall(slot)}
        onAddParticipant={(targetUser) => addParticipantToCall(targetUser)}
        addingParticipant={addingParticipant}
        onGenerateInviteLink={generateInviteLink}
        generatingLink={generatingLink}
        showInviteLink={showInviteLink}
        inviteLink={inviteLink}
        onCopyInviteLink={copyInviteLink}
        onCloseInviteLink={() => { setShowInviteLink(false); setInviteLink(null); }}
        onHapticFeedback={callNotifications.hapticFeedback}
        isDark={isDark}
        primaryColor={primaryColor}
        showParticipantsPanel={showParticipantsPanel}
        onToggleParticipantsPanel={setShowParticipantsPanel}
        showActivityLog={showActivityLog}
        onToggleActivityLog={setShowActivityLog}
        activityLog={activityLog}
        activeReactions={activeReactions}
        participantHandRaises={participantHandRaises}
        handsRaisedCount={participantHandRaises.size}
        waitingParticipants={waitingParticipants}
        onAdmitParticipant={admitFromLobby}
        onDenyParticipant={denyFromLobby}
        isBreakoutActive={isBreakoutActive}
        breakoutRooms={breakoutRooms}
        onShowBreakoutModal={() => setShowBreakoutModal(true)}
        meetingSettingsConfig={meetingSettingsConfig}
        showWhiteboard={showWhiteboard}
        onToggleWhiteboard={toggleWhiteboard}
        remotePresenterBoardActive={Boolean(
          remotePresenterInfo &&
            user?.id != null &&
            remotePresenterInfo.userId !== user.id &&
            !showWhiteboard
        )}
        remotePresenterBoardName={
          remotePresenterInfo && user?.id != null && remotePresenterInfo.userId !== user.id
            ? remotePresenterInfo.userName
            : undefined
        }
        isSipCall={isSipCall}
        isGateCall={isGateCall}
        onSendDtmf={isSipCall || isGateCall ? sendDtmf : undefined}
        onShowKeypad={() => {
          setViewMode('keypad');
          setActiveTab('keypad');
        }}
        activeSessions={activeSessions}
        loadingSessions={loadingSessions}
        onTransferToDevice={(sessionId) => transferCallToDevice(sessionId)}
        transferringTo={transferringTo}
        extensionList={extensionList}
        loadingExtensions={loadingExtensions}
        pendingTransfer={pendingBlindTransfer}
        onCancelTransfer={
          attendedTransferState.status === 'ringing' ||
          attendedTransferState.status === 'consulting'
            ? cancelAttendedTransfer
            : cancelBlindTransfer
        }
        attendedTransferState={attendedTransferState}
        attendedTransferSubmitting={attendedTransferSubmitting}
        onCompleteTransfer={completeAttendedTransfer}
        onMergeConference={isSipCall ? mergeConsultationToConference : undefined}
        onCancelAttendedTransfer={cancelAttendedTransfer}
        secondLineIncoming={secondLineIncoming}
        onAnswerSecondLine={answerSecondLineCall}
        onDeclineSecondLine={declineSecondLineCall}
        activeCallLines={activeCallLines}
        onSelectActiveCallLine={(lineId) => {
          void selectActiveCallLine(lineId);
        }}
        operationsUsers={operationsUsers}
        isUserOnline={isUserOnline}
        parkingSlots={parkingSlots}
        loadingParkingSlots={loadingParkingSlots}
        selectedParticipantMenu={selectedParticipantMenu}
        onSelectParticipantMenu={setSelectedParticipantMenu}
        onRequestParticipantMute={(id, name) => {
          if (!callIdRef.current) return;
          wsClient.send({
            type: 'request_participant_mute',
            call_id: callIdRef.current,
            target_participant_id: id,
            target_participant_name: name,
            requested_by: user?.id
          });
          toast(`Mute request sent to ${name}`);
        }}
        onRemoveParticipant={(id, name) => {
          if (!callIdRef.current) return;
          wsClient.send({
            type: 'remove_participant',
            call_id: callIdRef.current,
            target_participant_id: id,
            target_participant_name: name,
            removed_by: user?.id
          });
          toast(`${name} removed from call`);
        }}
        callStartTime={
          focusedLine === 'secondary' &&
          secondaryElapsedStartRef.current != null
            ? secondaryElapsedStartRef.current
            : callStartTimeRef.current || 0
        }
        layoutVariant={layoutVariant}
      />
      {meetingOverlays}
      </>
    );
  }

  // ============================================
  // Render: Contacts/Recents/Keypad View
  // ============================================
  const isMobileApp = isCapacitor();
  // Raised above CapacitorFooter (z-[90]) — the modal now owns the full screen height.
  const backdropZIndexClass = 'z-[99]';
  const panelZIndexClass = 'z-[100]';
  const idleHeaderTitle =
    activeTab === 'keypad'
      ? 'Phone'
      : activeTab === 'recents'
        ? 'History'
        : activeTab === 'meetings'
          ? 'Meetings'
          : 'Contacts';
  const IdleHeaderIcon =
    activeTab === 'keypad'
      ? Phone
      : activeTab === 'recents'
        ? Clock
        : activeTab === 'meetings'
          ? Video
          : Users;
  const idleHeaderActionClass = `inline-flex items-center justify-center rounded-lg border p-2 transition-colors ${
    isDark
      ? 'border-gray-700 bg-gray-900/50 text-gray-500 hover:border-gray-600 hover:text-gray-300'
      : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-600'
  }`;

  // Embedded desktop: dial companion (team / CRM / recents) belongs beside the keypad only.
  // Contacts & recents tabs already render the full toolbar + list in the main column — showing
  // the companion there duplicated "ALL CONTACTS" side-by-side (see product UX).
  const idleSplitAsideNode =
    layoutVariant === 'embedded' && !isMobileApp ? (
      activeTab === 'keypad' ? (
        <TotalConnectIdleDialCompanion
          isDark={isDark}
          primaryColor={primaryColor}
          showCrmTab={showCrmContactsTab}
          operationsUsers={operationsUsers}
          loadingUsers={loadingUsers}
          contactsSort={contactsSort}
          isUserOnline={isUserOnline}
          getUserLastSeen={getUserLastSeen}
          currentUserId={user?.id}
          canAccessPhoneContacts={canAccessContacts()}
          onOpenPhoneContacts={() => setShowPhoneContacts(true)}
          onStartTeamCall={startCall}
          callLogs={callLogs}
          loadingCallLogs={loadingCallLogs}
          recentsScope={recentsScope}
          onRecentsScopeChange={setRecentsScope}
          recentsTimeSort={recentsTimeSort}
          onCallBack={(userId, callType) => {
            const targetUser = operationsUsers.find((u) => u.id === userId);
            if (targetUser) void startCall(targetUser, callType);
          }}
          onDialIntoKeypad={(number) => {
            setDialPadNumber(number);
            setActiveTab('keypad');
            setViewMode('keypad');
          }}
        />
      ) : (
        <TotalConnectIdleStageAside
          isDark={isDark}
          primaryColor={primaryColor}
          organizationName={clientBranding?.trading_name ?? null}
          hasSipExtension={hasSipExtension}
          extensionNumber={callSettings?.sip_extension?.extension_number}
        />
      )
    ) : null;

  const embeddedLegacyHeaderHidden = layoutVariant === 'embedded' && !isMobileApp;

  return (
    <>
    <TotalConnectRtcIdleShell
      onBackdropClose={handleClose}
      isMobileApp={isMobileApp}
      showMobileTopSpacer={isMobileApp}
      backdropZIndexClass={backdropZIndexClass}
      panelZIndexClass={panelZIndexClass}
      isDark={isDark}
      variant={layoutVariant}
      idleSplitAside={idleSplitAsideNode}
      header={
        embeddedLegacyHeaderHidden ? null : (
          <CapacitorModalFilterHeader
            icon={IdleHeaderIcon}
            title={idleHeaderTitle}
            titleExtra={<UserDocsLink docsPath="/total-connect/calls" label="User guide: Calls" />}
            isDark={isDark}
            onClose={handleClose}
            actions={
              <>
                <button
                  type="button"
                  onClick={() => setShowCallSettings(true)}
                  className={`relative ${idleHeaderActionClass}`}
                  title="Settings"
                  aria-label="Settings"
                >
                  <Settings className="h-4 w-4" />
                  {isDNDEnabled && (
                    <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
                  )}
                </button>
              </>
            }
          />
        )
      }
      scrollContent={
        <>
          {layoutVariant === 'embedded' && !isMobileApp && (
            <div className={`border-b px-3 py-2 ${isDark ? 'border-gray-800 bg-gray-900/70' : 'border-gray-200 bg-white/80'}`}>
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                {[
                  { label: 'Conference', value: 'Invite, merge & manage participants' },
                  { label: 'Dual SIP lines', value: 'Primary + consultation LiveKit rooms' },
                  { label: 'Video tools', value: 'Screen share, whiteboard, reactions' },
                  { label: 'Enterprise', value: 'Record, park, transfer, voicemail' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-800 bg-gray-950/50' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {item.label}
                    </p>
                    <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(activeTab === 'contacts' || activeTab === 'recents') && (
            <>
              {hasMultiplePabxClients && (
                <div
                  className={`flex items-center gap-2 border-b px-2 py-1.5 ${
                    isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <span
                    className={`flex-shrink-0 text-[10px] font-medium uppercase tracking-wide ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {activeTab === 'recents' ? 'History from' : 'Calling from'}
                  </span>
                  <TotalConnectClientSelector
                    clients={accessiblePabxClients}
                    selectedClientId={selectedPabxClientId}
                    onSelect={(id) => {
                      selectPabxClient(id);
                      setRecentsAllClients(false);
                    }}
                    isDark={isDark}
                    primaryColor={primaryColor}
                    allowAllOption={activeTab === 'recents'}
                    allSelected={activeTab === 'recents' && recentsAllClients}
                    onSelectAll={() => {
                      setRecentsAllClients(true);
                    }}
                    disabled={callState !== 'idle'}
                  />
                </div>
              )}
              <TotalConnectCallsListToolbar
                activeTab={activeTab === 'contacts' ? 'contacts' : 'recents'}
                isDark={isDark}
                primaryColor={primaryColor}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                allowCrmContacts={showCrmContactsTab}
                contactsSubTab={contactsSubTab}
                onContactsSubTabChange={setContactsSubTab}
                contactsSort={contactsSort}
                onContactsSortChange={setContactsSort}
                recentsScope={recentsScope}
                onRecentsScopeChange={setRecentsScope}
                recentsTimeSort={recentsTimeSort}
                onRecentsTimeSortChange={setRecentsTimeSort}
                canAccessPhoneContacts={canAccessContacts()}
                onOpenPhoneContacts={() => setShowPhoneContacts(true)}
              />
            </>
          )}
          {activeTab === 'contacts' && (
            <>
              {(!showKeypad || contactsSubTab === 'team') && (
                <ContactsView
                  users={operationsUsers}
                  loading={loadingUsers}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onStartCall={startCall}
                  isUserOnline={isUserOnline}
                  getUserLastSeen={getUserLastSeen}
                  isDark={isDark}
                  primaryColor={primaryColor}
                  currentUserId={user?.id}
                  canAccessPhoneContacts={canAccessContacts()}
                  onOpenPhoneContacts={() => setShowPhoneContacts(true)}
                  hideSearchBar
                  sortMode={contactsSort}
                />
              )}
              {showCrmContactsTab && contactsSubTab === 'crm' && (
                <CrmDialContactsList
                  isDark={isDark}
                  primaryColor={primaryColor}
                  onSelectPhone={(number, _displayLabel) => {
                    setDialPadNumber(number);
                    setActiveTab('keypad');
                    setViewMode('keypad');
                  }}
                  onOpenContact={() => {
                    router.push('/client-zone/modules/total-crm?tab=contacts');
                  }}
                  toolbarSearchQuery={searchQuery}
                  onToolbarSearchChange={setSearchQuery}
                  nameSort={contactsSort === 'name_desc' ? 'desc' : 'asc'}
                />
              )}
            </>
          )}
          
          {activeTab === 'recents' && (
            <RecentsView
              callLogs={callLogs}
              loading={loadingCallLogs}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onCallBack={(userId, type) => {
                const targetUser = operationsUsers.find(u => u.id === userId);
                if (targetUser) startCall(targetUser, type);
              }}
              onDialNumber={(number) => {
                setDialPadNumber(number);
                setActiveTab('keypad');
                setViewMode('keypad');
              }}
              currentUserId={user?.id}
              isDark={isDark}
              primaryColor={primaryColor}
              recentsScope={recentsScope}
              onRecentsScopeChange={setRecentsScope}
              recentsTimeSort={recentsTimeSort}
              hideRecentsToolbar
            />
          )}
          
          {activeTab === 'keypad' && (
            <DialPadView
              extension={callSettings?.sip_extension || null}
              dialNumber={dialPadNumber}
              onDialNumberChange={setDialPadNumber}
              onDial={dialPadSubmit}
              onCancelOutboundDial={cancelOutboundDialInProgress}
              isDialing={isDialingPSTN}
              isDark={isDark}
              primaryColor={primaryColor}
              inCall={false}
              onSendDtmf={isSipCall ? sendDtmf : undefined}
              dialSuggestions={dialPadSuggestions}
            />
          )}

          {activeTab === 'meetings' && (
            <TotalConnectMeetingsView
              isDark={isDark}
              primaryColor={primaryColor}
              meetingSettings={meetingSettingsConfig}
              startingMeeting={creatingInstantMeeting}
              onStartMeeting={() => void startInstantMeeting()}
              onScheduleMeeting={() => {
                setShowScheduleMeetingModal(true);
                // EventModal renders at z-[95] — above CapacitorFooter but
                // still below the widget panel (z-100), so close it first.
                handleClose();
              }}
              onJoinMeeting={handleJoinMeetingTarget}
              upcomingMeetings={upcomingMeetingEvents}
              onOpenMeetingLink={handleOpenMeetingLink}
            />
          )}
        </>
      }
      idleTabBar={
        callState === 'idle' ? (
          <div
            data-tour-id="tcon-calls-tabs"
            className={`flex-shrink-0 border-t ${isDark ? 'border-gray-700/50 bg-gray-900/95' : 'border-gray-200 bg-white/95'} backdrop-blur-sm`}
          >
            <div className="flex items-end justify-around px-6 pb-1 pt-2">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('contacts');
                  setViewMode('contacts');
                }}
                className={`flex w-16 flex-col items-center gap-0.5 py-1 transition-colors ${
                  activeTab === 'contacts'
                    ? 'text-blue-600 dark:text-blue-400'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                }`}
                title="Contacts"
                aria-label="Contacts"
              >
                <Users className="h-5 w-5" />
                <span className="text-[10px] font-medium">Contacts</span>
              </button>

              {showKeypad && (
                <button
                  type="button"
                  onClick={() => {
                    if (activeTab === 'keypad') {
                      setActiveTab('meetings');
                      setViewMode('meetings');
                    } else {
                      setActiveTab('keypad');
                      setViewMode('keypad');
                    }
                  }}
                  className="flex flex-col items-center gap-1 transition-transform active:scale-95"
                  title={activeTab === 'keypad' ? 'Video' : 'Phone'}
                  aria-label={activeTab === 'keypad' ? 'Video meetings' : 'Phone keypad'}
                >
                  <span
                    className={`flex h-14 w-14 items-center justify-center rounded-full shadow-lg ring-1 transition-colors ${
                      activeTab === 'keypad' || activeTab === 'meetings'
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white ring-blue-600/50'
                        : isDark
                          ? 'bg-gray-800 text-gray-300 ring-gray-700'
                          : 'bg-white text-gray-600 ring-gray-200'
                    }`}
                  >
                    {activeTab === 'keypad' ? (
                      <Video className="h-6 w-6" />
                    ) : (
                      <Grid3X3 className="h-6 w-6" />
                    )}
                  </span>
                  <span
                    className={`text-[10px] font-medium ${
                      activeTab === 'keypad' || activeTab === 'meetings'
                        ? 'text-blue-600 dark:text-blue-400'
                        : isDark
                          ? 'text-gray-400'
                          : 'text-gray-500'
                    }`}
                  >
                    {activeTab === 'keypad' ? 'Video' : 'Phone'}
                  </span>
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setActiveTab('recents');
                  setViewMode('recents');
                  void markMissedCallsAsViewed();
                }}
                className={`flex w-16 flex-col items-center gap-0.5 py-1 transition-colors ${
                  activeTab === 'recents'
                    ? 'text-blue-600 dark:text-blue-400'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                }`}
                title="History"
                aria-label="History"
              >
                <span className="relative">
                  <Clock className="h-5 w-5" />
                  {missedCallsCount > 0 && (
                    <span className="absolute -right-1.5 -top-1 flex min-h-[14px] min-w-[14px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white">
                      {missedCallsCount > 9 ? '9+' : missedCallsCount}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium">History</span>
              </button>
            </div>
          </div>
        ) : null
      }
      nonIdleBottomSpacer={null}
      overlays={
        <>
        {/* Call Settings Panel Overlay */}
        {showCallSettings && (
          <CallSettingsPanel
            callSettings={callSettings}
            isDNDEnabled={isDNDEnabled}
            dndLoading={dndLoading}
            onToggleDND={toggleDND}
            hasSipExtension={hasSipExtension}
            sipInboundMobileOnly={callSettings?.sip_inbound_mobile_only === true}
            sipInboundMobileOnlyLoading={sipInboundMobileOnlyLoading}
            onToggleSipInboundMobileOnly={toggleSipInboundMobileOnly}
            accentColor={primaryColor}
            onClose={() => setShowCallSettings(false)}
            onDialSpeedDial={(number) => {
              setShowCallSettings(false);
              setDialPadNumber(number);
              setActiveTab('keypad');
              setViewMode('keypad');
            }}
            onOpenVoicemail={hasSipExtension ? () => setShowVoicemail(true) : undefined}
            isDark={isDark}
          />
        )}
        
        {/* Phone Contacts Modal Overlay */}
        <PhoneContactsModal
          isOpen={showPhoneContacts}
          onClose={() => setShowPhoneContacts(false)}
          onSelectNumber={(number) => {
            setDialPadNumber(number);
            setShowPhoneContacts(false);
            if (showKeypad) {
              setActiveTab('keypad');
              setViewMode('keypad');
            }
          }}
          isDark={isDark}
          primaryColor={primaryColor}
        />
        
        {/* Voicemail Panel Overlay */}
        {showVoicemail && (
          <VoicemailPanel
            onClose={() => setShowVoicemail(false)}
            isDark={isDark}
            primaryColor={primaryColor}
            extensionId={callSettings?.sip_extension?.id}
            extensionNumber={callSettings?.sip_extension?.extension_number}
          />
        )}
        
        {/* Whiteboard Overlay - only if allowed in settings */}
        {meetingSettingsConfig.allowWhiteboard && (
          <CallWhiteboard
            isOpen={showWhiteboard}
            onClose={() => toggleWhiteboard(false)}
            userId={String(user?.id || 0)}
            userName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
            roomId={callIdRef.current || 'unknown'}
            onSendDrawing={handleSendDrawing}
            remoteElements={whiteboardElements}
            readOnly={whiteboardUIMode.kind === 'following'}
            followerPresenterName={
              whiteboardUIMode.kind === 'following' ? whiteboardUIMode.userName : null
            }
          />
        )}
        
        {/* Breakout Rooms Modal */}
        <BreakoutRoomsModal
          isOpen={showBreakoutModal}
          onClose={() => setShowBreakoutModal(false)}
          participants={callSession.participants.map((p: CallParticipant) => ({
            id: p.userId,
            name: p.name,
            avatar: p.avatar_url
          }))}
          onStartBreakout={startBreakoutRooms}
          onEndBreakout={endBreakoutRooms}
          isBreakoutActive={isBreakoutActive}
          activeRooms={breakoutRooms}
          currentUserId={user?.id || 0}
          isHost={true}
        />
        </>
      }
    />
    {meetingOverlays}
    </>
  );
}
